// src/pages/Personeel.js
// vPER-069
// - Documentsets: optioneel onderdeel zonder gekoppeld document toont Optioneel in plaats van Gereed
// vPER-068
// - Documentsetonderdelen: bestaand document kiezen of direct nieuw document uploaden en koppelen
// vPER-064
// - Gestarte documentset kan worden verwijderd en opnieuw gestart; documenten zelf blijven behouden
// vPER-063
// - Terugkoppeling vanuit Actiecentrum: juiste medewerker selecteren en Documentsets-popup automatisch openen
// vPER-062
// - Knoppenvolgorde rechterkolom aangepast: Matching / Documentsets, Opslaan / Verwijderen, Vernieuw rechts
// vPER-061
// - Documentsets-knop verplaatst naar rechter mutatiekolom en vaste okerkleur gegeven
// vPER-060
// - Documentsets gekoppeld aan geselecteerd personeelslid
// vPER-059
// - Bedrijfsnaam bij ZZP/Doorleen kan vrij worden ingevuld of uit alle actieve Organisaties worden gekozen
// - Bij keuze bestaande Organisatie: KvK, BTW-nummer en BTW-tarief automatisch overnemen
// vPER-058
// - Inzetvorm Vast/Tijdelijk/ZZP/Doorleen toegevoegd
// - Rechterkolom wisselt tussen loonvelden en tarief/leveranciersvelden
// - Optionele koppeling ZZP/Doorleen aan bestaande Relatie
// - Grid toont Inzetvorm en Uurloon/tarief
// vPER-057
//
// StaffIQ HRM - Personeel
// Layout afgestemd op Relaties / Contactpersonen / Notities:
// - Links: zoek + knoppen
// - Midden: overzicht met kolomfilters
// - Rechts: detailkaart
// - CV-import zet opleidingen/werkervaring/certificaten/talen/competenties klaar als Matching gegevens; alleen overige info naar Opmerking
// - Opslaan blokkeert dubbele klik en toont tijdens verwerking een duidelijke melding
// - Onderin detailkaart: Matching gegevens
// - Vervoer uitgebreid met Eigen / Van de zaak; beide keuzes sluiten elkaar uit
// - Personeel: communicatie/akkoord via WhatsApp en E-mail

import React, { useEffect, useMemo, useRef, useState } from "react";
import useVoiceCommands from "../components/useVoiceCommands";
import HelpButton from "../components/HelpButton";
import PersoneelDossierPopup from "../components/PersoneelDossierPopup";

const PHOTO_CROP_BOX_W = 240;
const PHOTO_CROP_BOX_H = 320;
const PHOTO_OUTPUT_W = 300;
const PHOTO_OUTPUT_H = 400;

const buildUrl = (u) =>
  u.startsWith("http") ? u : `${u.startsWith("/") ? u : `/${u}`}`;

function getParam(name) {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get(name) || "";
  } catch {
    return "";
  }
}

function norm(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[，、]/g, ",")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[;:!?]/g, "")
    .replace(/\s+/g, " ");
}

function safeTrim(s) {
  return String(s || "").trim();
}

function joinSp(parts, sep = " ") {
  return parts.map((x) => safeTrim(x)).filter(Boolean).join(sep).trim();
}

function isEmptyWord(t) {
  const x = norm(t);
  return x === "geen" || x === "blanco" || x === "leeg" || x === "null";
}

function uniqSorted(arr) {
  const set = new Set((arr || []).map((x) => String(x).trim()).filter(Boolean));
  return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
}

function parseActiveValue(it) {
  const raw = it?.active ?? it?.is_active ?? it?.isActive ?? it?.actief;
  if (raw === undefined || raw === null || raw === "") return null;
  if (raw === true) return true;
  if (raw === false) return false;
  const s = String(raw).toLowerCase().trim();
  if (raw === 1 || s === "1" || s === "true" || s === "yes" || s === "y" || s === "j" || s === "ja" || s === "aan" || s === "on") return true;
  if (raw === 0 || s === "0" || s === "false" || s === "no" || s === "n" || s === "nee" || s === "uit" || s === "off") return false;
  return !!raw;
}

function boolToJaNee(v) {
  return v === true ? "j" : "n";
}

function formatDateNL(iso) {
  const s = String(iso || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y}`;
}

function isoDate(s) {
  const t = String(s || "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : "";
}

function normalizeCvDate(value) {
  const raw = safeTrim(value);
  if (!raw) return "";

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const nl = raw.match(/^(\d{1,2})[-/.\s](\d{1,2})[-/.\s](\d{4})$/);
  if (nl) {
    const dd = String(nl[1]).padStart(2, "0");
    const mm = String(nl[2]).padStart(2, "0");
    const yyyy = String(nl[3]);
    return `${yyyy}-${mm}-${dd}`;
  }

  return "";
}

function cvPick(data, key) {
  const v = data?.[key];
  if (v === null || v === undefined) return "";
  return safeTrim(v);
}

function cvBool(value) {
  if (value === true) return true;
  if (value === false) return false;

  const s = safeTrim(value).toLowerCase();
  if (!s) return false;

  return ["true", "1", "ja", "j", "yes", "y", "aanwezig", "beschikbaar"].includes(s);
}

function parseDecimal(v) {
  if (typeof v === "number") {
    return Number.isFinite(v) ? v : 0;
  }

  let raw = String(v ?? "")
    .trim()
    .replace(/\s/g, "")
    .replace(/€/g, "");

  if (!raw) return 0;

  const lastComma = raw.lastIndexOf(",");
  const lastDot = raw.lastIndexOf(".");

  if (lastComma >= 0 && lastDot >= 0) {
    if (lastComma > lastDot) {
      // Nederlandse notatie: 6.500,00
      raw = raw.replace(/\./g, "").replace(",", ".");
    } else {
      // Internationale notatie: 6,500.00
      raw = raw.replace(/,/g, "");
    }
  } else if (lastComma >= 0) {
    // Nederlandse decimale komma: 6500,00
    raw = raw.replace(",", ".");
  } else if (lastDot >= 0) {
    const dotCount = (raw.match(/\./g) || []).length;
    const decimals = raw.length - lastDot - 1;

    if (dotCount > 1 || decimals > 2) {
      // Punt als duizendtalscheiding: 6.500
      raw = raw.replace(/\./g, "");
    }
    // Eén punt met 1 of 2 cijfers erachter blijft decimaal: 55.00
  }

  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function moneyInput(v) {
  if (v === null || v === undefined || v === "") return "";

  const n = parseDecimal(v);
  if (!Number.isFinite(n)) return "";

  return n.toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatMoney(val) {
  const n = Number(val || 0);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

async function tryFetchJson(urls, options = {}) {
  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  const tenant =
    sessionStorage.getItem("tenant") ||
    localStorage.getItem("tenant") ||
    "demo";

  let role = "user";
  try {
    role =
      JSON.parse(
        sessionStorage.getItem("user") ||
        localStorage.getItem("user") ||
        "{}"
      )?.role || "user";
  } catch {}

  const companyCodeIds =
    sessionStorage.getItem("staffiq_company_code_ids") ||
    localStorage.getItem("staffiq_company_code_ids") ||
    "";

  let lastErr = null;

  for (const url of urls) {
    try {
      const headers = {
        ...(options.headers || {}),
        Authorization: token ? `Bearer ${token}` : undefined,
        "x-tenant": tenant,
        "x-role": role,
        "x-company-code-ids": companyCodeIds || undefined,
      };

      Object.keys(headers).forEach(
        (k) => headers[k] === undefined && delete headers[k]
      );

      const res = await fetch(buildUrl(url), {
        ...options,
        headers,
      });

      if (!res.ok) {
        const raw = await res.text();
        let msg = `${url} (${res.status})`;

        try {
          const data = raw ? JSON.parse(raw) : null;
          msg = data?.error || data?.message || raw || msg;
        } catch {
          msg = raw || msg;
        }

        throw new Error(msg);
      }

      if (res.status === 204) return null;
      const text = await res.text();
      if (!text) return null;

      try {
        return JSON.parse(text);
      } catch {
        return text;
      }
    } catch (err) {
      lastErr = err;
    }
  }

  throw lastErr;
}

function normalizePhoneForCommunication(raw, defaultCountryCode = "+31") {
  let s = String(raw || "").trim();

  if (!s || s === "-") return "";

  s = s
    .replace(/[^\d+]/g, "")
    .replace(/^00/, "+");

  if (!s) return "";

  if (s.startsWith("+")) return s;

  if (s.startsWith("0")) {
    return `${defaultCountryCode}${s.slice(1)}`;
  }

  if (s.startsWith("31")) {
    return `+${s}`;
  }

  return `${defaultCountryCode}${s}`;
}

async function loadCommunicationSettingsSafe() {
  try {
    const data = await tryFetchJson(["/api/communication-settings"]);

    return {
      email_enabled: data?.email_enabled !== false,
      whatsapp_enabled: data?.whatsapp_enabled !== false,
      sms_enabled: data?.sms_enabled === true,
      default_country_code: data?.default_country_code || "+31",
      default_sms_text: data?.default_sms_text || "",
      default_whatsapp_text: data?.default_whatsapp_text || "",
    };
  } catch {
    return {
      email_enabled: true,
      whatsapp_enabled: true,
      sms_enabled: false,
      default_country_code: "+31",
      default_sms_text: "",
      default_whatsapp_text: "",
    };
  }
}

function stopGridClick(e) {
  try {
    e.preventDefault();
    e.stopPropagation();
  } catch {}
}

function askCommunicationChoice(raw) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");

    overlay.style.position = "fixed";
    overlay.style.left = "0";
    overlay.style.top = "0";
    overlay.style.right = "0";
    overlay.style.bottom = "0";
    overlay.style.background = "rgba(0,0,0,0.35)";
    overlay.style.zIndex = "999999";
    overlay.style.display = "flex";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.padding = "18px";
    overlay.style.boxSizing = "border-box";

    const box = document.createElement("div");

    box.style.width = "min(520px, 100%)";
    box.style.background = "#fff";
    box.style.borderRadius = "12px";
    box.style.boxShadow = "0 18px 50px rgba(0,0,0,0.35)";
    box.style.padding = "18px";
    box.style.boxSizing = "border-box";
    box.style.fontFamily = "Arial, Helvetica, sans-serif";
    box.style.color = "#111";

    const title = document.createElement("div");
    title.style.fontWeight = "700";
    title.style.fontSize = "18px";
    title.style.marginBottom = "12px";
    title.textContent = `Wat wil je doen met ${raw}?`;

    const help = document.createElement("div");
    help.style.fontSize = "16px";
    help.style.lineHeight = "1.8";
    help.style.marginBottom = "12px";
    help.innerHTML = "1 = Bellen<br/>2 = SMS<br/>3 = WhatsApp";

    const input = document.createElement("input");
    input.type = "text";
    input.value = "3";
    input.inputMode = "numeric";
    input.autocomplete = "off";
    input.style.width = "100%";
    input.style.fontSize = "20px";
    input.style.padding = "10px 12px";
    input.style.border = "2px solid #1f4e79";
    input.style.borderRadius = "8px";
    input.style.boxSizing = "border-box";
    input.style.marginBottom = "14px";

    const buttons = document.createElement("div");
    buttons.style.display = "flex";
    buttons.style.gap = "10px";
    buttons.style.justifyContent = "flex-end";

    const okButton = document.createElement("button");
    okButton.type = "button";
    okButton.textContent = "OK";
    okButton.style.padding = "10px 18px";
    okButton.style.border = "none";
    okButton.style.borderRadius = "8px";
    okButton.style.background = "#1f4e79";
    okButton.style.color = "#fff";
    okButton.style.fontSize = "16px";
    okButton.style.cursor = "pointer";

    const cancelButton = document.createElement("button");
    cancelButton.type = "button";
    cancelButton.textContent = "Annuleren";
    cancelButton.style.padding = "10px 18px";
    cancelButton.style.border = "1px solid #d0d0d0";
    cancelButton.style.borderRadius = "8px";
    cancelButton.style.background = "#fff";
    cancelButton.style.color = "#111";
    cancelButton.style.fontSize = "16px";
    cancelButton.style.cursor = "pointer";

    let done = false;

    function cleanup(value) {
      if (done) return;
      done = true;

      try {
        document.body.removeChild(overlay);
      } catch {}

      resolve(value);
    }

    okButton.onclick = () => cleanup(input.value);
    cancelButton.onclick = () => cleanup(null);

    input.onkeydown = (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        cleanup(input.value);
      }

      if (ev.key === "Escape") {
        ev.preventDefault();
        cleanup(null);
      }
    };

    overlay.onclick = (ev) => {
      if (ev.target === overlay) {
        cleanup(null);
      }
    };

    buttons.appendChild(cancelButton);
    buttons.appendChild(okButton);

    box.appendChild(title);
    box.appendChild(help);
    box.appendChild(input);
    box.appendChild(buttons);

    overlay.appendChild(box);
    document.body.appendChild(overlay);

    setTimeout(() => {
      try {
        input.focus();
        input.select();
      } catch {}
    }, 80);
  });
}

async function handleEmailGridClick(e, email) {
  stopGridClick(e);

  const mail = String(email || "").trim();

  if (!mail || mail === "-") return;

  const settings = await loadCommunicationSettingsSafe();

  if (settings.email_enabled === false) {
    alert("E-mail is niet actief in Communicatie-instellingen.");
    return;
  }

  window.location.href = `mailto:${encodeURIComponent(mail)}`;
}

function handlePhoneGridClick(e, phone) {
  stopGridClick(e);

  const tel = String(phone || "").trim();

  if (!tel || tel === "-") return;

  window.location.href = `tel:${tel}`;
}

async function handleMobileGridClick(e, mobile) {
  stopGridClick(e);

  const raw = String(mobile || "").trim();

  if (!raw || raw === "-") return;

  const settings = await loadCommunicationSettingsSafe();
  const normalized = normalizePhoneForCommunication(raw, settings.default_country_code || "+31");

  if (!normalized) return;

  const choice = await askCommunicationChoice(raw);

  const c = String(choice || "").trim().toLowerCase();

  if (!c) return;

  if (c === "1" || c === "bellen" || c === "bel") {
    window.location.href = `tel:${raw}`;
    return;
  }

  if (c === "2" || c === "sms") {
    if (settings.sms_enabled !== true) {
      alert("SMS is niet actief in Communicatie-instellingen.");
      return;
    }

    const text = window.prompt(
      `SMS naar ${raw}`,
      settings.default_sms_text || ""
    );

    if (text === null) return;

    const message = String(text || "").trim();

    if (!message) {
      alert("SMS tekst ontbreekt.");
      return;
    }

    try {
      await tryFetchJson(["/api/communications/sms"], {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: raw,
          text: message,
        }),
      });

      alert(`SMS verstuurd naar ${raw}`);
    } catch (err) {
      alert(err?.message || "SMS verzenden mislukt.");
    }

    return;
  }

  if (c === "3" || c === "whatsapp" || c === "app") {
    if (settings.whatsapp_enabled === false) {
      alert("WhatsApp is niet actief in Communicatie-instellingen.");
      return;
    }

    const whatsappNumber = normalized.replace(/[^\d]/g, "");
    const text = String(settings.default_whatsapp_text || "").trim();
    const suffix = text ? `?text=${encodeURIComponent(text)}` : "";

    window.open(`https://wa.me/${whatsappNumber}${suffix}`, "_blank", "noopener,noreferrer");
  }
}

function upperInitialsFromFirstName(firstName) {
  const t = safeTrim(firstName);
  if (!t) return "";
  return t
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => (p[0] ? `${p[0].toUpperCase()}.` : ""))
    .join("");
}

function buildSearchName(data) {
  const firstName = safeTrim(data.first_name || "");
  const initials = safeTrim(data.initials || "");
  const prefix = safeTrim(data.name_prefix || "");
  const lastName = safeTrim(data.last_name || "");

  const firstPart = firstName || initials;
  const afterComma = joinSp([firstPart, prefix]);

  if (lastName && afterComma) return `${lastName}, ${afterComma}`;
  if (lastName) return lastName;
  return afterComma;
}

function emptyMatchingRow() {
  return {
    id: null,
    category: "",
    name: "",
    description: "",
    level: "",
    date_from: "",
    date_to: "",
    diploma: false,
    employer: "",
  };
}

function companyCodeLabel(row) {
  return safeTrim(row?.company_code_name || row?.company_code || row?.bedrijfscode || "");
}

function selectedCompanyCodeIdsFromScope() {
  const raw =
    sessionStorage.getItem("staffiq_company_code_ids") ||
    localStorage.getItem("staffiq_company_code_ids") ||
    "";

  return String(raw || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function selectedCompanyCodesForForm(companyCodes = []) {
  const activeCodes = (companyCodes || []).filter((cc) => cc?.active !== false);
  const selectedIds = selectedCompanyCodeIdsFromScope();

  if (!selectedIds.length) return activeCodes;

  const selectedSet = new Set(selectedIds.map(String));
  const filtered = activeCodes.filter((cc) => selectedSet.has(String(cc.id)));

  return filtered.length ? filtered : activeCodes;
}

function firstCompanyCodeIdFromScope(companyCodes = []) {
  const selected = selectedCompanyCodesForForm(companyCodes);
  const first = selected[0];
  return first?.id ? String(first.id) : "";
}

const emptyForm = {
  id: null,
  company_code_id: "",

  // naam / privé
  first_name: "",
  initials: "",
  name_prefix: "",
  last_name: "",
  gender: "",
  birth_date: "",
  salutation: "",
  attention_to: "",
  search_name: "",

  // HRM basis
  employment_type: "Vast",
  source_organisation_id: "",
  status: "Actief",
  job_functions: "",
  marital_status: "",
  birth_place: "",
  birth_country: "",
  nationality: "",

  employment_start_date: "",
  employment_end_date: "",
  hourly_wage: "",
  sales_rate: "",
  monthly_wage: "",
  rate_level: "",

  // ID / documenten
  id_type: "",
  id_number: "",
  id_expiry_date: "",
  bsn: "",
  residence_permit: "",
  driving_license: "",
  own_transport: false,
  company_transport: false,
  transport_description: "",

  // adres / contact
  visit_street: "",
  visit_house_number: "",
  visit_addition: "",
  visit_postcode: "",
  visit_city: "",
  visit_province: "",
  visit_region: "",
  visit_country: "",

  corr_street: "",
  corr_house_number: "",
  corr_addition: "",
  corr_postcode: "",
  corr_city: "",
  corr_province: "",
  corr_region: "",
  corr_country: "",

  phone: "",
  mobile: "",
  email: "",
  website: "",

  correspondence_whatsapp: false,
  correspondence_email: false,
  post_digital: true,
  mailing_list: true,

  // ZZP / Doorleen tariefgegevens (bestaande kolommen hergebruikt)
  zzp: false,
  zzp_company_name: "",
  zzp_kvk: "",
  zzp_vat_number: "",
  zzp_vat_rate: "",
  zzp_purchase_rate: "",
  zzp_sales_rate: "",

  own_insurance: false,
  insurance_company: "",
  insurance_name: "",
  insurance_number: "",

  remarks: "",
  profile_photo_path: "",
  active: true,
};

export default function Personeel() {
  const rawUser =
    sessionStorage.getItem("user") ||
    localStorage.getItem("user") ||
    "";

  let role = "user";

  try {
    role = JSON.parse(rawUser || "{}")?.role || "user";
  } catch {}

  const isReadonlyRole = String(role || "").toLowerCase() === "readonly";
  const VERSION = "vPER-069";

  const [items, setItems] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dossierOpen, setDossierOpen] = useState(false);
  const [documentSetsOpen, setDocumentSetsOpen] = useState(false);
  const [documentSets, setDocumentSets] = useState([]);
  const [documentSetInstances, setDocumentSetInstances] = useState([]);
  const [personnelDocuments, setPersonnelDocuments] = useState([]);
  const [documentSetsBusy, setDocumentSetsBusy] = useState(false);
  const [documentSetsError, setDocumentSetsError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [matchingRows, setMatchingRows] = useState([]);
  const [matchingDraft, setMatchingDraft] = useState(emptyMatchingRow());
  const [matchingEditId, setMatchingEditId] = useState(null);
  const [matchingOpen, setMatchingOpen] = useState(false);
  const [remarkEditor, setRemarkEditor] = useState(false);

  const [photoEditorOpen, setPhotoEditorOpen] = useState(false);
  const [photoSrc, setPhotoSrc] = useState("");
  const [photoNatural, setPhotoNatural] = useState({ width: 0, height: 0 });
  const [photoZoom, setPhotoZoom] = useState(1);
  const [photoOffsetX, setPhotoOffsetX] = useState(0);
  const [photoOffsetY, setPhotoOffsetY] = useState(0);
  const [photoSaving, setPhotoSaving] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const [photoVersion, setPhotoVersion] = useState(Date.now());
  const [pendingProfilePhotoData, setPendingProfilePhotoData] = useState("");

  const [cvImportBusy, setCvImportBusy] = useState(false);
  const [cvImportMessage, setCvImportMessage] = useState("");
  const [cvImportResultOpen, setCvImportResultOpen] = useState(false);
  const [cvImportError, setCvImportError] = useState("");
  const [pendingCvMatchingRows, setPendingCvMatchingRows] = useState([]);
  const cvFileInputRef = useRef(null);

  const [listIndex, setListIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [saveError, setSaveError] = useState(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const saveInProgressRef = useRef(false);
  const [flash, setFlash] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [speechInfo, setSpeechInfo] = useState("");

  const [q, setQ] = useState(getParam("q"));

  const EMPTY_VAL = "__EMPTY__";
  const [searchNameFilter, setSearchNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [functionFilter, setFunctionFilter] = useState("");
  const [pcFilter, setPcFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const [mobileFilter, setMobileFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [employmentTypeFilter, setEmploymentTypeFilter] = useState("");
  const [wageFilter, setWageFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("j");
  const [companyCodeFilter, setCompanyCodeFilter] = useState("");

const [mode, setMode] = useState("search");
  const isFormLocked = isReadonlyRole || mode !== "form";
  const [sort, setSort] = useState({ key: "search", dir: "asc" });

  const [touchedCorr, setTouchedCorr] = useState({
    corr_street: false,
    corr_house_number: false,
    corr_addition: false,
    corr_postcode: false,
    corr_city: false,
    corr_province: false,
    corr_region: false,
    corr_country: false,
  });

  const pageTopRef = useRef(null);
  const searchRef = useRef(null);
  const rowRefs = useRef([]);
  const midScrollRef = useRef(null);
  const rightScrollRef = useRef(null);

  const firstNameRef = useRef(null);
  const initialsRef = useRef(null);
  const namePrefixRef = useRef(null);
  const lastNameRef = useRef(null);
  const genderRef = useRef(null);
  const birthDateRef = useRef(null);
  const salutationRef = useRef(null);
  const attentionToRef = useRef(null);
  const searchNameRef = useRef(null);

  const statusRef = useRef(null);
  const functionsRef = useRef(null);
  const maritalRef = useRef(null);

  const phoneRef = useRef(null);
  const mobileRef = useRef(null);
  const emailRef = useRef(null);

  const remarkSmallRef = useRef(null);
  const remarkBigRef = useRef(null);
  const matchingSectionRef = useRef(null);
  const matchingFormRef = useRef(null);
  const matchingInputRef = useRef(null);

  const photoInputRef = useRef(null);
  const photoCropImageRef = useRef(null);

  // voorkomt dat programmatic focus op klein Opmerking-veld meteen weer groot opent
  const ignoreNextRemarkSmallFocusRef = useRef(false);

  const focusOrderRefs = useMemo(
    () => [
      firstNameRef,
      initialsRef,
      namePrefixRef,
      lastNameRef,
      genderRef,
      birthDateRef,
      salutationRef,
      attentionToRef,
      searchNameRef,
      statusRef,
      functionsRef,
      maritalRef,
      phoneRef,
      mobileRef,
      emailRef,
    ],
    []
  );

  function formHasId() {
    return form.id !== null && form.id !== undefined && String(form.id).length > 0;
  }

  function photoUrl(id = form.id) {
    if (!id) return "";
    return buildUrl(`/api/personnel/${encodeURIComponent(id)}/photo?v=${photoVersion}`);
  }

  function openPhotoPicker() {
    setPhotoError("");

    try {
      photoInputRef.current?.click?.();
    } catch {}
  }

  function handlePhotoFile(file) {
    setPhotoError("");

    if (!file) return;

    if (!String(file.type || "").startsWith("image/")) {
      setPhotoError("Kies een afbeelding, bijvoorbeeld jpg, png, bmp of webp.");
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      setPhotoSrc(String(reader.result || ""));
      setPhotoNatural({ width: 0, height: 0 });
      setPhotoZoom(1);
      setPhotoOffsetX(0);
      setPhotoOffsetY(0);
      setPhotoEditorOpen(true);
    };

    reader.onerror = () => {
      setPhotoError("Afbeelding kon niet worden gelezen.");
    };

    reader.readAsDataURL(file);
  }

  function getPhotoCropMetrics() {
    const img = photoCropImageRef.current;
    const naturalW = Number(photoNatural.width || img?.naturalWidth || 0);
    const naturalH = Number(photoNatural.height || img?.naturalHeight || 0);

    if (!naturalW || !naturalH) {
      return {
        boxW: PHOTO_CROP_BOX_W,
        boxH: PHOTO_CROP_BOX_H,
        outW: PHOTO_OUTPUT_W,
        outH: PHOTO_OUTPUT_H,
        drawWInBox: PHOTO_CROP_BOX_W,
        drawHInBox: PHOTO_CROP_BOX_H,
        drawXInBox: 0,
        drawYInBox: 0,
      };
    }

    const baseScale = Math.max(
      PHOTO_CROP_BOX_W / naturalW,
      PHOTO_CROP_BOX_H / naturalH
    );

    const finalScale = baseScale * Number(photoZoom || 1);
    const drawWInBox = naturalW * finalScale;
    const drawHInBox = naturalH * finalScale;
    const drawXInBox = (PHOTO_CROP_BOX_W - drawWInBox) / 2 + Number(photoOffsetX || 0);
    const drawYInBox = (PHOTO_CROP_BOX_H - drawHInBox) / 2 + Number(photoOffsetY || 0);

    return {
      boxW: PHOTO_CROP_BOX_W,
      boxH: PHOTO_CROP_BOX_H,
      outW: PHOTO_OUTPUT_W,
      outH: PHOTO_OUTPUT_H,
      drawWInBox,
      drawHInBox,
      drawXInBox,
      drawYInBox,
    };
  }

  async function saveCroppedPhoto() {
    if (!photoSrc) {
      setPhotoError("Geen foto gekozen.");
      return;
    }

    const img = photoCropImageRef.current;

    if (!img || !img.naturalWidth || !img.naturalHeight) {
      setPhotoError("Foto is nog niet geladen.");
      return;
    }

    try {
      setPhotoSaving(true);
      setPhotoError("");

      const {
        boxW,
        boxH,
        outW,
        outH,
        drawWInBox,
        drawHInBox,
        drawXInBox,
        drawYInBox,
      } = getPhotoCropMetrics();

      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;

      const ctx = canvas.getContext("2d");

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, outW, outH);

      const ratioX = outW / boxW;
      const ratioY = outH / boxH;

      ctx.drawImage(
        img,
        drawXInBox * ratioX,
        drawYInBox * ratioY,
        drawWInBox * ratioX,
        drawHInBox * ratioY
      );

      const imageData = canvas.toDataURL("image/png");

      if (!formHasId()) {
        setPendingProfilePhotoData(imageData);
        setForm((prev) => ({
          ...prev,
          profile_photo_path: "__pending__",
        }));
        setPhotoEditorOpen(false);
        setPhotoSrc("");
        setPhotoNatural({ width: 0, height: 0 });
        setFlash("Pasfoto klaargezet. Deze wordt opgeslagen na Opslaan ✅");
        setTimeout(() => setFlash(""), 1600);
        return;
      }

      const saved = await tryFetchJson(
        [`/api/personnel/${form.id}/photo`],
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ image_data: imageData }),
        }
      );

      setForm((prev) => ({
        ...prev,
        profile_photo_path: saved?.profile_photo_path || prev.profile_photo_path || "pasfoto.png",
      }));

      setItems((prev) =>
        prev.map((x) =>
          String(x.id) === String(form.id)
            ? { ...x, profile_photo_path: saved?.profile_photo_path || "pasfoto.png" }
            : x
        )
      );

      setPhotoVersion(Date.now());
      setPhotoEditorOpen(false);
      setPhotoSrc("");
      setPhotoNatural({ width: 0, height: 0 });
      setFlash("Pasfoto opgeslagen ✅");
      setTimeout(() => setFlash(""), 900);
    } catch (e) {
      setPhotoError(e?.message || String(e));
    } finally {
      setPhotoSaving(false);
    }
  }

  async function deletePhoto() {
    if (!formHasId() && pendingProfilePhotoData) {
      setPendingProfilePhotoData("");
      setForm((prev) => ({ ...prev, profile_photo_path: "" }));
      setFlash("Klaargezette pasfoto verwijderd ✅");
      setTimeout(() => setFlash(""), 900);
      return;
    }

    if (!formHasId()) return;

    if (!window.confirm("Pasfoto verwijderen?")) return;

    try {
      setPhotoError("");

      await tryFetchJson(
        [`/api/personnel/${form.id}/photo`],
        { method: "DELETE" }
      );

      setPendingProfilePhotoData("");
      setForm((prev) => ({ ...prev, profile_photo_path: "" }));
      setItems((prev) =>
        prev.map((x) =>
          String(x.id) === String(form.id)
            ? { ...x, profile_photo_path: "" }
            : x
        )
      );

      setPhotoVersion(Date.now());
      setFlash("Pasfoto verwijderd ✅");
      setTimeout(() => setFlash(""), 900);
    } catch (e) {
      setPhotoError(e?.message || String(e));
    }
  }

  function closeRemarkEditorToSmall() {
    const panel = rightScrollRef.current;
    const panelTop = panel ? panel.scrollTop : 0;

    setRemarkEditor(false);

    requestAnimationFrame(() => {
      try {
        if (panel) panel.scrollTop = panelTop;
      } catch {}

      requestAnimationFrame(() => {
        try {
          const el = remarkSmallRef.current;
          if (!el) return;

          ignoreNextRemarkSmallFocusRef.current = true;

          try {
            el.focus?.({ preventScroll: true });
          } catch {
            el.focus?.();
          }

          if (panel) {
            try {
              const top = el.offsetTop;
              const bottom = top + el.offsetHeight;
              const viewTop = panel.scrollTop;
              const viewBottom = viewTop + panel.clientHeight;
              const pad = 8;

              if (top < viewTop + pad) {
                panel.scrollTop = Math.max(0, top - pad);
              } else if (bottom > viewBottom - pad) {
                panel.scrollTop = Math.max(0, bottom - panel.clientHeight + pad);
              }
            } catch {}
          }
        } catch {}
      });
    });
  }

  function openRemarkEditorAndFocusEnd() {
    setRemarkEditor(true);

    setTimeout(() => {
      try {
        const el = remarkBigRef.current;
        if (!el) return;

        try {
          el.focus?.({ preventScroll: true });
        } catch {
          el.focus?.();
        }

        const len = String(el.value ?? "").length;
        el.setSelectionRange?.(len, len);
      } catch {}
    }, 0);
  }

useEffect(() => {
    setForm((prev) => {
      const next = { ...prev };
      let changed = false;

      if (!safeTrim(next.initials) && safeTrim(next.first_name)) {
        next.initials = upperInitialsFromFirstName(next.first_name);
        changed = true;
      }

      const search = buildSearchName(next);
      if (search && next.search_name !== search) {
        next.search_name = search;
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [
    form.first_name,
    form.initials,
    form.name_prefix,
    form.last_name,
  ]);

  // WhatsApp/E-mail-akkoord mag direct worden aangevinkt.
  // Bij het verzenden van accordering wordt later gecontroleerd of mobiel/e-mail echt gevuld is.

  useEffect(() => {
    setForm((prev) => {
      const next = { ...prev };
      let changed = false;

      const mappings = [
        ["visit_street", "corr_street"],
        ["visit_house_number", "corr_house_number"],
        ["visit_addition", "corr_addition"],
        ["visit_postcode", "corr_postcode"],
        ["visit_city", "corr_city"],
        ["visit_province", "corr_province"],
        ["visit_region", "corr_region"],
        ["visit_country", "corr_country"],
      ];

      mappings.forEach(([fromKey, toKey]) => {
        if (touchedCorr[toKey]) return;

        const fromValue = prev[fromKey] || "";

        if ((next[toKey] || "") !== fromValue) {
          next[toKey] = fromValue;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [
    form.visit_street,
    form.visit_house_number,
    form.visit_addition,
    form.visit_postcode,
    form.visit_city,
    form.visit_province,
    form.visit_region,
    form.visit_country,
    touchedCorr,
  ]);

  // ---------- LOAD COMPANY CODES ----------
  useEffect(() => {
    let cancelled = false;

    async function loadCompanyCodes() {
      try {
        const data = await tryFetchJson(["/api/company-scope"]);
        const codes = Array.isArray(data?.company_codes) ? data.company_codes : [];
        if (!cancelled) setCompanyCodes(codes);
      } catch {
        if (!cancelled) setCompanyCodes([]);
      }
    }

    async function loadOrganisations() {
      try {
        // Alle actieve organisaties binnen de toegestane bedrijfscodes.
        // Geen filter op Klant / Prospect / Partner / Leverancier.
        const data = await tryFetchJson(["/api/organisations?activeOnly=1"]);
        const rows = Array.isArray(data) ? data : [];

        if (!cancelled) {
          setOrganisations(
            rows.filter((o) => parseActiveValue(o) !== false)
          );
        }
      } catch {
        if (!cancelled) setOrganisations([]);
      }
    }

    loadCompanyCodes();
    loadOrganisations();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- LOAD ITEMS ----------
  const serverQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (activeFilter === "j") p.set("active", "1");
    else if (activeFilter === "n") p.set("active", "0");
    return p.toString();
  }, [q, activeFilter]);

  useEffect(() => {
    let cancelled = false;

    async function loadItems() {
      setLoading(true);
      setError(null);

      try {
        const data = await tryFetchJson([
          serverQuery ? `/api/personnel?${serverQuery}` : "/api/personnel",
          serverQuery ? `/api/personeel?${serverQuery}` : "/api/personeel",
        ]);

        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadItems();

    return () => {
      cancelled = true;
    };
  }, [serverQuery, reloadKey]);

  // ---------- OPTIONS ----------
function employmentTypeOf(item = {}) {
    const raw = safeTrim(item?.employment_type || item?.inzetvorm || "");
    if (["Vast", "Tijdelijk", "ZZP", "Doorleen"].includes(raw)) return raw;
    return item?.zzp === true ? "ZZP" : "Vast";
  }

  function organisationDisplayName(o = {}) {
    return safeTrim(o.search_name || o.name || o.naam || o.bedrijfsnaam || "");
  }

  const employmentTypeOptions = ["Vast", "Tijdelijk", "ZZP", "Doorleen"];
  const isPayrollEmployment = form.employment_type === "Vast" || form.employment_type === "Tijdelijk";
  const isExternalEmployment = form.employment_type === "ZZP" || form.employment_type === "Doorleen";

  function handleExternalCompanyNameChange(value) {
    const typed = String(value ?? "");
    const wanted = safeTrim(typed).toLocaleLowerCase("nl-NL");

    const selected = organisations.find((o) => {
      const name = organisationDisplayName(o).toLocaleLowerCase("nl-NL");
      return name && name === wanted;
    });

    if (!selected) {
      // Vrije bedrijfsnaam: geen verplichte Relatie-koppeling.
      setForm((prev) => ({
        ...prev,
        source_organisation_id: "",
        zzp_company_name: typed,
      }));
      return;
    }

    // Bestaande actieve Organisatie gekozen:
    // neem beschikbare stamgegevens over.
    setForm((prev) => ({
      ...prev,
      source_organisation_id: String(selected.id),
      zzp_company_name: organisationDisplayName(selected),
      zzp_kvk: safeTrim(selected.kvk),
      zzp_vat_number: safeTrim(selected.vat_number || selected.btw_nummer),
      zzp_vat_rate:
        selected.vat_rate === null || selected.vat_rate === undefined
          ? ""
          : safeTrim(selected.vat_rate),
    }));
  }

  const searchNameOptions = useMemo(() => uniqSorted(items.map((x) => x.search_name || "")), [items]);
  const employmentTypeGridOptions = useMemo(() => uniqSorted(items.map((x) => employmentTypeOf(x))), [items]);
  const statusOptions = useMemo(() => uniqSorted(items.map((x) => x.status || "")), [items]);
  const functionOptions = useMemo(() => uniqSorted(items.map((x) => x.job_functions || "")), [items]);

  const maritalStatusOptions = useMemo(() => uniqSorted(items.map((x) => x.marital_status || "")), [items]);
  const birthPlaceOptions = useMemo(() => uniqSorted(items.map((x) => x.birth_place || "")), [items]);
  const birthCountryOptions = useMemo(() => uniqSorted(items.map((x) => x.birth_country || "")), [items]);
  const nationalityOptions = useMemo(() => uniqSorted(items.map((x) => x.nationality || "")), [items]);

  const idTypeOptions = useMemo(() => uniqSorted(items.map((x) => x.id_type || "")), [items]);
  const residencePermitOptions = useMemo(() => uniqSorted(items.map((x) => x.residence_permit || "")), [items]);
  const drivingLicenseOptions = useMemo(() => uniqSorted(items.map((x) => x.driving_license || "")), [items]);

  const visitStreetOptions = useMemo(() => uniqSorted(items.map((x) => x.visit_street || "")), [items]);
  const visitCityOptions = useMemo(() => uniqSorted(items.map((x) => x.visit_city || "")), [items]);
  const visitProvinceOptions = useMemo(() => uniqSorted(items.map((x) => x.visit_province || "")), [items]);
  const visitRegionOptions = useMemo(() => uniqSorted(items.map((x) => x.visit_region || "")), [items]);
  const visitCountryOptions = useMemo(() => uniqSorted(items.map((x) => x.visit_country || "")), [items]);

  const corrStreetOptions = useMemo(() => uniqSorted(items.map((x) => x.corr_street || "")), [items]);
  const pcOptions = useMemo(() => uniqSorted(items.map((x) => x.corr_postcode || "")), [items]);
  const cityOptions = useMemo(() => uniqSorted(items.map((x) => x.corr_city || "")), [items]);
  const corrProvinceOptions = useMemo(() => uniqSorted(items.map((x) => x.corr_province || "")), [items]);
  const regionOptions = useMemo(() => uniqSorted(items.map((x) => x.corr_region || "")), [items]);
  const corrCountryOptions = useMemo(() => uniqSorted(items.map((x) => x.corr_country || "")), [items]);

  const zzpVatRateOptions = useMemo(() => uniqSorted(items.map((x) => x.zzp_vat_rate || "")), [items]);
  const insuranceCompanyOptions = useMemo(() => uniqSorted(items.map((x) => x.insurance_company || "")), [items]);
  const insuranceNameOptions = useMemo(() => uniqSorted(items.map((x) => x.insurance_name || "")), [items]);

  const matchingCategoryOptions = useMemo(
    () =>
      uniqSorted([
        "Opleiding",
        "Werkervaring",
        "Certificaten",
        "Competenties",
        "Talenkennis",
        ...matchingRows.map((x) => x.category || ""),
      ]),
    [matchingRows]
  );

  const phoneOptions = useMemo(() => uniqSorted(items.map((x) => x.phone || "")), [items]);
  const mobileOptions = useMemo(() => uniqSorted(items.map((x) => x.mobile || "")), [items]);
  const emailOptions = useMemo(() => uniqSorted(items.map((x) => x.email || "")), [items]);
  const wageOptions = useMemo(() => uniqSorted(items.map((x) => String(x.hourly_wage ?? ""))), [items]);
  const companyCodeOptions = useMemo(() => uniqSorted(items.map((x) => companyCodeLabel(x))), [items]);

  const filteredItems = useMemo(() => {
    const qq = norm(q);
    const arr = items.slice();

    const match = (val, filt) => {
      if (!filt) return true;
      if (filt === EMPTY_VAL) return !safeTrim(val);
      return String(val) === String(filt);
    };

    return arr.filter((it) => {
      const hay = norm([
        it.search_name,
        it.status,
        it.job_functions,
        it.corr_postcode,
        it.corr_city,
        it.corr_region,
        it.phone,
        it.mobile,
        it.email,
        it.rate_level,
        employmentTypeOf(it),
        it.zzp_company_name,
        companyCodeLabel(it),
      ].join(" "));

      if (qq && !hay.includes(qq)) return false;
      if (!match(it.search_name || "", searchNameFilter)) return false;
      if (!match(it.status || "", statusFilter)) return false;
      if (!match(it.job_functions || "", functionFilter)) return false;
      if (!match(it.corr_postcode || "", pcFilter)) return false;
      if (!match(it.corr_city || "", cityFilter)) return false;
      if (!match(it.corr_region || "", regionFilter)) return false;
      if (!match(it.phone || "", phoneFilter)) return false;
      if (!match(it.mobile || "", mobileFilter)) return false;
      if (!match(it.email || "", emailFilter)) return false;
      if (!match(String(it.hourly_wage ?? ""), wageFilter)) return false;
      if (!match(companyCodeLabel(it), companyCodeFilter)) return false;

      if (!match(employmentTypeOf(it), employmentTypeFilter)) return false;

      const active = parseActiveValue(it);
      if (activeFilter === "j" && active === false) return false;
      if (activeFilter === "n" && active !== false) return false;

      return true;
    });
  }, [
    items,
    q,
    searchNameFilter,
    statusFilter,
    functionFilter,
    pcFilter,
    cityFilter,
    regionFilter,
    phoneFilter,
    mobileFilter,
    emailFilter,
    employmentTypeFilter,
    wageFilter,
    companyCodeFilter,
    activeFilter,
  ]);

const sortedItems = useMemo(() => {
    const copy = filteredItems.slice();
    const dirMul = sort.dir === "desc" ? -1 : 1;

    const cmp = (a, b) => {
      const aa = norm(a);
      const bb = norm(b);
      if (aa === bb) return 0;
      return aa < bb ? -1 : 1;
    };

    copy.sort((a, b) => {
      if (sort.key === "search") return dirMul * cmp(a.search_name || "", b.search_name || "");
      if (sort.key === "status") return dirMul * cmp(a.status || "", b.status || "");
      if (sort.key === "function") return dirMul * cmp(a.job_functions || "", b.job_functions || "");
      if (sort.key === "pc") return dirMul * cmp(a.corr_postcode || "", b.corr_postcode || "");
      if (sort.key === "city") return dirMul * cmp(a.corr_city || "", b.corr_city || "");
      if (sort.key === "region") return dirMul * cmp(a.corr_region || "", b.corr_region || "");
      if (sort.key === "phone") return dirMul * cmp(a.phone || "", b.phone || "");
      if (sort.key === "mobile") return dirMul * cmp(a.mobile || "", b.mobile || "");
      if (sort.key === "email") return dirMul * cmp(a.email || "", b.email || "");
      if (sort.key === "employment_type") return dirMul * cmp(employmentTypeOf(a), employmentTypeOf(b));
      if (sort.key === "wage") {
        const av = ["ZZP", "Doorleen"].includes(employmentTypeOf(a)) ? Number(a.zzp_purchase_rate || 0) : Number(a.hourly_wage || 0);
        const bv = ["ZZP", "Doorleen"].includes(employmentTypeOf(b)) ? Number(b.zzp_purchase_rate || 0) : Number(b.hourly_wage || 0);
        return dirMul * (av - bv);
      }
      if (sort.key === "active") return dirMul * cmp(String(parseActiveValue(a) ? "1" : "0"), String(parseActiveValue(b) ? "1" : "0"));
      if (sort.key === "company_code") return dirMul * cmp(companyCodeLabel(a), companyCodeLabel(b));
      return dirMul * cmp(a.search_name || "", b.search_name || "");
    });

    return copy;
  }, [filteredItems, sort]);

  const gridCount = Math.min(sortedItems.length, 9999);

const sortedMatchingRows = useMemo(() => {
    const copy = Array.isArray(matchingRows) ? matchingRows.slice() : [];

    copy.sort((a, b) => {
      const catA = norm(a.category || "");
      const catB = norm(b.category || "");

      if (catA !== catB) return catA < catB ? -1 : 1;

      const dateA = String(a.date_from || "").slice(0, 10);
      const dateB = String(b.date_from || "").slice(0, 10);

      const blankA = !dateA;
      const blankB = !dateB;

      if (blankA !== blankB) return blankA ? -1 : 1;
      if (dateA !== dateB) return dateA < dateB ? -1 : 1;

      return Number(a.id || 0) - Number(b.id || 0);
    });

    return copy;
  }, [matchingRows]);

  function itBoolText(v) {
    return v === true ? "j" : "n";
  }

  function sortToggle(key) {
    setSort((s) => {
      if (s.key !== key) return { key, dir: "asc" };
      return { key, dir: s.dir === "asc" ? "desc" : "asc" };
    });
  }

  function sortArrow(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  }

  function resetFilters() {
    setSearchNameFilter("");
    setStatusFilter("");
    setFunctionFilter("");
    setPcFilter("");
    setCityFilter("");
    setRegionFilter("");
    setPhoneFilter("");
    setMobileFilter("");
    setEmailFilter("");
    setEmploymentTypeFilter("");
    setWageFilter("");
    setActiveFilter("j");
    setCompanyCodeFilter("");
  }

function resetRightPanel() {
    setSelectedId(null);
    setForm({ ...emptyForm, company_code_id: firstCompanyCodeIdFromScope(companyCodes), employment_type: "Vast", source_organisation_id: "" });
    setMatchingRows([]);
    setMatchingDraft(emptyMatchingRow());
    setMatchingEditId(null);
    setMatchingOpen(false);
    setRemarkEditor(false);
    setPhotoEditorOpen(false);
    setPhotoSrc("");
    setPhotoNatural({ width: 0, height: 0 });
    setPhotoError("");
    setPendingProfilePhotoData("");
    setCvImportBusy(false);
    setCvImportMessage("");
    setCvImportResultOpen(false);
    setCvImportError("");
    setPendingCvMatchingRows([]);
    setSaveError(null);

    setTouchedCorr({
      corr_street: false,
      corr_house_number: false,
      corr_addition: false,
      corr_postcode: false,
      corr_city: false,
      corr_province: false,
      corr_region: false,
      corr_country: false,
    });
  }

function positionSearchNoKeyboard() {
    setTimeout(() => {
      try {
        if (rightScrollRef.current) {
          rightScrollRef.current.scrollTop = 0;
        }
      } catch {}

      try {
        searchRef.current?.focus?.();
        searchRef.current?.select?.();
      } catch {}

      try {
        if (midScrollRef.current) {
          midScrollRef.current.scrollTop = 0;
        }
      } catch {}
    }, 0);
  }

  function positionRightPanelTopNoKeyboard() {
    setTimeout(() => {
      try {
        if (document.activeElement && typeof document.activeElement.blur === "function") {
          document.activeElement.blur();
        }
      } catch {}

      try {
        const panel = rightScrollRef.current;
        if (!panel) return;

        panel.scrollTop = 0;

        if (window.innerWidth <= 768) {
          panel.scrollIntoView?.({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        }
      } catch {}
    }, 80);
  }

function positionPageTopNoKeyboard() {
    setTimeout(() => {
      try {
        if (document.activeElement && typeof document.activeElement.blur === "function") {
          document.activeElement.blur();
        }
      } catch {}

      try {
        if (rightScrollRef.current) {
          rightScrollRef.current.scrollTop = 0;
        }
      } catch {}

      try {
        if (midScrollRef.current) {
          midScrollRef.current.scrollTop = 0;
        }
      } catch {}

      try {
        const target = pageTopRef.current;
        if (!target) return;

        target.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });

        setTimeout(() => {
          try {
            const menuRuimte = 90;
            const rect = target.getBoundingClientRect();
            const verschil = rect.top - menuRuimte;

            if (Math.abs(verschil) < 3) return;

            const scrollers = [];

            if (document.scrollingElement) {
              scrollers.push(document.scrollingElement);
            }

            if (document.documentElement) {
              scrollers.push(document.documentElement);
            }

            if (document.body) {
              scrollers.push(document.body);
            }

            let el = target.parentElement;

            while (el) {
              try {
                if (el.scrollHeight > el.clientHeight) {
                  scrollers.push(el);
                }
              } catch {}

              el = el.parentElement;
            }

            scrollers.forEach((scroller) => {
              try {
                scroller.scrollTop += verschil;
              } catch {}
            });
          } catch {}
        }, 260);
      } catch {}
    }, 80);
  }

function positionMatchingBlockNoKeyboard() {
    setTimeout(() => {
      try {
        if (document.activeElement && typeof document.activeElement.blur === "function") {
          document.activeElement.blur();
        }
      } catch {}

      try {
        matchingSectionRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      } catch {}
    }, 80);
  }

  function positionMatchingFormAndFocusCategory() {
    setTimeout(() => {
      try {
        matchingFormRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      } catch {}

      setTimeout(() => {
        try {
          matchingInputRef.current?.focus?.();
          matchingInputRef.current?.select?.();
        } catch {}
      }, 220);
    }, 120);
  }

  function positionMatchingHeaderNoKeyboard() {
    setTimeout(() => {
      try {
        if (document.activeElement && typeof document.activeElement.blur === "function") {
          document.activeElement.blur();
        }
      } catch {}

      try {
        matchingSectionRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      } catch {}
    }, 120);
  }

  function openMatchingBlock() {
    if (!formHasId()) {
      setSaveError("Sla de medewerker eerst op voordat je matchinggegevens toevoegt.");
      return;
    }

    setSaveError("");
    setMatchingOpen(true);
    positionMatchingBlockNoKeyboard();
  }

  function closeMatchingBlock() {
    setMatchingEditId(null);
    setMatchingDraft(emptyMatchingRow());
    setMatchingOpen(false);
    positionRightPanelTopNoKeyboard();
  }

  function vernieuwList() {
    setQ("");
    resetFilters();
    resetRightPanel();
    setListIndex(-1);
    setMode("search");
    setReloadKey((k) => k + 1);
    setFlash("Vernieuwd ✅");
    setTimeout(() => setFlash(""), 900);

    setTimeout(() => {
      try {
        if (document.activeElement && typeof document.activeElement.blur === "function") {
          document.activeElement.blur();
        }
      } catch {}

      try {
        if (rightScrollRef.current) {
          rightScrollRef.current.scrollTop = 0;
        }
      } catch {}

      try {
        if (midScrollRef.current) {
          midScrollRef.current.scrollTop = 0;
        }
      } catch {}
    }, 0);
  }
  function newItem() {
    resetRightPanel();

    setForm({
      ...emptyForm,
      company_code_id: firstCompanyCodeIdFromScope(companyCodes),
      employment_type: "Vast",
      source_organisation_id: "",
      zzp: false,
      status: "Actief",
      correspondence_whatsapp: false,
      correspondence_email: false,
      post_digital: true,
      mailing_list: true,
      active: true,
    });

    setMode("form");
    setListIndex(-1);

    setTimeout(() => {
      try {
        rightScrollRef.current?.scrollTo?.({ top: 0 });
        firstNameRef.current?.focus?.();
      } catch {}
    }, 0);
  }

  function applyCvImport(data = {}) {
    const fields = data?.fields || data?.personnel || data || {};

    const education = Array.isArray(data?.education) ? data.education : [];
    const experience = Array.isArray(data?.work_experience) ? data.work_experience : [];
    const certificates = Array.isArray(data?.certificates) ? data.certificates : [];
    const languages = Array.isArray(data?.languages) ? data.languages : [];
    const skills = Array.isArray(data?.skills) ? data.skills : [];
    const unmappedInfo = Array.isArray(data?.unmapped_info) ? data.unmapped_info : [];
    const importedProfilePhotoData = safeTrim(data?.profile_photo_data);

    if (importedProfilePhotoData.startsWith("data:image/")) {
      setPendingProfilePhotoData(importedProfilePhotoData);
    }

    function cleanPendingMatchingRow(row = {}) {
      if (!row || typeof row !== "object" || Array.isArray(row)) return null;

      const clean = {
        category: safeTrim(row.category || row.categorie || "Algemeen"),
        name: safeTrim(row.name || row.naam || row.title || row.titel),
        description: safeTrim(row.description || row.omschrijving || row.details),
        level: safeTrim(row.level || row.niveau),
        date_from: normalizeCvDate(row.date_from || row.van || row.start_date),
        date_to: normalizeCvDate(row.date_to || row.tot || row.end_date),
        diploma: cvBool(row.diploma),
        employer: safeTrim(row.employer || row.werkgever || row.institute || row.instelling),
      };

      const hasValue =
        clean.category ||
        clean.name ||
        clean.description ||
        clean.level ||
        clean.date_from ||
        clean.date_to ||
        clean.diploma ||
        clean.employer;

      return hasValue ? clean : null;
    }

    function fallbackRows(category, rows) {
      return (Array.isArray(rows) ? rows : [])
        .map((value) => safeTrim(value))
        .filter(Boolean)
        .map((value) => ({
          category,
          name: value,
          description: "",
          level: "",
          date_from: "",
          date_to: "",
          diploma: category === "Certificaten",
          employer: "",
        }));
    }

    let importedMatchingRows = Array.isArray(data?.matching)
      ? data.matching.map(cleanPendingMatchingRow).filter(Boolean)
      : [];

    // Compatibiliteit wanneer tijdelijk nog een oudere serverversie actief is.
    if (!importedMatchingRows.length) {
      importedMatchingRows = [
        ...fallbackRows("Opleiding", education),
        ...fallbackRows("Werkervaring", experience),
        ...fallbackRows("Certificaten", certificates),
        ...fallbackRows("Talenkennis", languages),
        ...fallbackRows("Competenties", skills),
      ];
    }

    setPendingCvMatchingRows(importedMatchingRows);

    // Alles wat niet logisch in vaste Personeelsvelden of Matchinggegevens past,
    // bewaren we bij Opmerking. Buiten setForm gedefinieerd zodat de
    // importmelding dezelfde informatie veilig kan gebruiken.
    const importedRemarks = [
      safeTrim(fields.remarks),
      ...unmappedInfo.map((x) => safeTrim(x)).filter(Boolean),
    ].filter(Boolean).join("\n\n");

    setForm((prev) => {
      const next = { ...prev };

      function setIfValue(key, value, transform = (x) => x) {
        const raw = safeTrim(value);
        if (!raw) return;

        next[key] = transform(raw);
      }

      setIfValue("first_name", fields.first_name);
      setIfValue("initials", fields.initials);
      setIfValue("name_prefix", fields.name_prefix);
      setIfValue("last_name", fields.last_name);
      setIfValue("gender", fields.gender);
      setIfValue("birth_date", fields.birth_date, normalizeCvDate);
      setIfValue("salutation", fields.salutation);
      setIfValue("attention_to", fields.attention_to);
      setIfValue("search_name", fields.search_name);

      setIfValue("job_functions", fields.job_functions || fields.function || fields.function_name);
      setIfValue("birth_place", fields.birth_place);
      setIfValue("birth_country", fields.birth_country);
      setIfValue("nationality", fields.nationality);

      setIfValue("driving_license", fields.driving_license);
      if (fields.own_transport !== undefined && fields.own_transport !== null) {
        next.own_transport = cvBool(fields.own_transport);
      }

      setIfValue("visit_street", fields.visit_street);
      setIfValue("visit_house_number", fields.visit_house_number);
      setIfValue("visit_addition", fields.visit_addition);
      setIfValue("visit_postcode", fields.visit_postcode, (x) => x.toUpperCase().replace(/\s+/g, ""));
      setIfValue("visit_city", fields.visit_city);
      setIfValue("visit_province", fields.visit_province);
      setIfValue("visit_region", fields.visit_region);
      setIfValue("visit_country", fields.visit_country);

      setIfValue("corr_street", fields.corr_street || fields.visit_street);
      setIfValue("corr_house_number", fields.corr_house_number || fields.visit_house_number);
      setIfValue("corr_addition", fields.corr_addition || fields.visit_addition);
      setIfValue("corr_postcode", fields.corr_postcode || fields.visit_postcode, (x) => x.toUpperCase().replace(/\s+/g, ""));
      setIfValue("corr_city", fields.corr_city || fields.visit_city);
      setIfValue("corr_province", fields.corr_province || fields.visit_province);
      setIfValue("corr_region", fields.corr_region || fields.visit_region);
      setIfValue("corr_country", fields.corr_country || fields.visit_country);

      setIfValue("phone", fields.phone);
      setIfValue("mobile", fields.mobile);
      setIfValue("email", fields.email);
      setIfValue("website", fields.website || fields.linkedin);

      // Alleen informatie die niet logisch in vaste velden of Matching past, gaat naar Opmerking.
      if (importedRemarks) {
        next.remarks = safeTrim(next.remarks)
          ? `${safeTrim(next.remarks)}\n\n${importedRemarks}`
          : importedRemarks;
      }

      if (!safeTrim(next.initials) && safeTrim(next.first_name)) {
        next.initials = upperInitialsFromFirstName(next.first_name);
      }

      if (!safeTrim(next.search_name)) {
        next.search_name = buildSearchName(next);
      }

      return next;
    });

    setTouchedCorr((prev) => ({
      ...prev,
      corr_street: true,
      corr_house_number: true,
      corr_addition: true,
      corr_postcode: true,
      corr_city: true,
      corr_province: true,
      corr_region: true,
      corr_country: true,
    }));

    const filled = [
      fields.first_name || fields.last_name ? "naam" : "",
      fields.mobile ? "mobiel" : "",
      fields.email ? "e-mail" : "",
      fields.visit_city || fields.corr_city ? "adres/plaats" : "",
      fields.job_functions || fields.function_name ? "functie" : "",
      importedMatchingRows.length ? `${importedMatchingRows.length} matchingregel${importedMatchingRows.length === 1 ? "" : "s"}` : "",
      importedProfilePhotoData.startsWith("data:image/") ? "pasfoto" : "",
      importedRemarks ? "overige info bij Opmerking" : "",
    ].filter(Boolean);

    setCvImportMessage(
      filled.length
        ? `CV geanalyseerd. Ingevuld/klaargezet: ${filled.join(", ")}. Controleer de gegevens en klik daarna op Opslaan.`
        : "CV geanalyseerd. Er zijn geen duidelijke gegevens gevonden."
    );
    setCvImportResultOpen(true);

    setMode("form");
    setTimeout(() => {
      try {
        firstNameRef.current?.focus?.();
      } catch {}
    }, 80);
  }

  async function handleCvImportFileChange(e) {
    const file = e.target.files?.[0];

    if (!file) return;

    try {
      setCvImportBusy(true);
      setCvImportError("");
      setCvImportMessage("");

      const token =
        sessionStorage.getItem("token") ||
        localStorage.getItem("token") ||
        "";

      const tenant =
        sessionStorage.getItem("tenant") ||
        localStorage.getItem("tenant") ||
        "demo";

      let parsedUser = {};
      try {
        parsedUser = JSON.parse(
          sessionStorage.getItem("user") ||
          localStorage.getItem("user") ||
          "{}"
        );
      } catch {}

      const companyCodeIds =
        sessionStorage.getItem("staffiq_company_code_ids") ||
        localStorage.getItem("staffiq_company_code_ids") ||
        "";

      const data = new FormData();
      data.append("file", file);

      const headers = {
        Authorization: token ? `Bearer ${token}` : undefined,
        "x-tenant": tenant,
        "x-role": parsedUser?.role || "user",
        "x-staffiq-user-id": parsedUser?.id || undefined,
        "x-staffiq-user-email": parsedUser?.email || undefined,
        "x-staffiq-user-name": parsedUser?.display_name || parsedUser?.name || undefined,
        "x-company-code-ids": companyCodeIds || undefined,
      };

      Object.keys(headers).forEach(
        (k) => headers[k] === undefined && delete headers[k]
      );

      const res = await fetch(buildUrl("/api/ai/personnel/cv-extract"), {
        method: "POST",
        headers,
        body: data,
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(json?.error || json?.message || "CV-analyse mislukt.");
      }

      applyCvImport(json?.data || json || {});
    } catch (err) {
      setCvImportError(err?.message || "CV-analyse mislukt.");
      setCvImportMessage("");
      setCvImportResultOpen(true);
    } finally {
      setCvImportBusy(false);
      if (e?.target) e.target.value = "";
    }
  }

async function loadItemFromRow(it) {
    setSelectedId(it.id);
    setMode("form");
    setSaveError(null);
    setFlash("");

    try {
      const det = await tryFetchJson([
        `/api/personnel/${encodeURIComponent(it.id)}`,
        `/api/personeel/${encodeURIComponent(it.id)}`,
      ]);

      const person = det?.person || det?.personeel || det || it;
      const match = Array.isArray(det?.matching) ? det.matching : [];

      setForm({
        ...emptyForm,
        ...person,
        company_code_id: person.company_code_id ? String(person.company_code_id) : "",
        birth_date: isoDate(person.birth_date),
        employment_start_date: isoDate(person.employment_start_date),
        employment_end_date: isoDate(person.employment_end_date),
        id_expiry_date: isoDate(person.id_expiry_date),
        employment_type: employmentTypeOf(person),
        source_organisation_id: person.source_organisation_id ? String(person.source_organisation_id) : "",
        hourly_wage: moneyInput(person.hourly_wage),
        sales_rate: moneyInput(person.sales_rate),
        monthly_wage: moneyInput(person.monthly_wage),
        rate_level: person.rate_level ?? "",
        zzp_purchase_rate: moneyInput(person.zzp_purchase_rate),
        zzp_sales_rate: moneyInput(person.zzp_sales_rate),
        correspondence_whatsapp: person.correspondence_whatsapp === true,
        correspondence_email: person.correspondence_email === true,
        post_digital: person.post_digital === true,
        mailing_list: person.mailing_list === true,
        zzp: employmentTypeOf(person) === "ZZP",
        own_transport: person.own_transport === true,
        company_transport: person.company_transport === true,
        own_insurance: person.own_insurance === true,
        active: parseActiveValue(person) !== false,
      });

      setMatchingRows(match);
      setMatchingDraft(emptyMatchingRow());
      setMatchingEditId(null);
      setMatchingOpen(false);
      setPhotoEditorOpen(false);
      setPhotoSrc("");
      setPhotoError("");
      setPhotoVersion(Date.now());
    } catch (e) {
      setForm({
        ...emptyForm,
        ...it,
        company_code_id: it.company_code_id ? String(it.company_code_id) : "",
        birth_date: isoDate(it.birth_date),
        employment_start_date: isoDate(it.employment_start_date),
        employment_end_date: isoDate(it.employment_end_date),
        id_expiry_date: isoDate(it.id_expiry_date),
        employment_type: employmentTypeOf(it),
        source_organisation_id: it.source_organisation_id ? String(it.source_organisation_id) : "",
        hourly_wage: moneyInput(it.hourly_wage),
        sales_rate: moneyInput(it.sales_rate),
        monthly_wage: moneyInput(it.monthly_wage),
        zzp_purchase_rate: moneyInput(it.zzp_purchase_rate),
        zzp_sales_rate: moneyInput(it.zzp_sales_rate),
        zzp: employmentTypeOf(it) === "ZZP",
        correspondence_whatsapp: it.correspondence_whatsapp === true,
        correspondence_email: it.correspondence_email === true,
        active: parseActiveValue(it) !== false,
      });
      setMatchingRows([]);
      setMatchingDraft(emptyMatchingRow());
      setMatchingEditId(null);
      setMatchingOpen(false);
      setPhotoEditorOpen(false);
      setPhotoSrc("");
      setPhotoError("");
      setPhotoVersion(Date.now());
    }

    setTimeout(() => {
      try {
        const panel = rightScrollRef.current;
        if (!panel) return;

        // Eerst rechter paneel intern naar boven
        try {
          panel.scrollTop = 0;
        } catch {}

        if (window.innerWidth <= 768) {
          // Op smartphone: wel naar rechter invoerkolom,
          // maar NIET automatisch focussen, zodat toetsenbord dicht blijft.
          panel.scrollIntoView?.({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });

          if (document.activeElement && typeof document.activeElement.blur === "function") {
            document.activeElement.blur();
          }

          return;
        }

        // Desktop: oude gedrag behouden
        firstNameRef.current?.focus?.();
      } catch {}
    }, 120);
  }


  useEffect(() => {
    let reopenId = "";
    let reopenDocumentSets = false;

    try {
      reopenId = sessionStorage.getItem("staffiq_reopen_personnel_id") || "";
      reopenDocumentSets = sessionStorage.getItem("staffiq_reopen_personnel_documentsets") === "1";
    } catch {}

    if (!reopenId || !items.length) return;

    const row = items.find((item) => String(item?.id || "") === String(reopenId));
    if (!row) return;

    try {
      sessionStorage.removeItem("staffiq_reopen_personnel_id");
      sessionStorage.removeItem("staffiq_reopen_personnel_documentsets");
    } catch {}

    (async () => {
      await loadItemFromRow(row);
      if (reopenDocumentSets) {
        setDocumentSetsOpen(true);
        await loadPersonnelDocumentSets(reopenId);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  async function loadPersonnelDocumentSets(personnelId = selectedId) {
    if (!personnelId) return;
    setDocumentSetsBusy(true); setDocumentSetsError("");
    try {
      const [sets, instances, docs] = await Promise.all([
        tryFetchJson(["/api/document-sets"]),
        tryFetchJson([`/api/document-set-instances?source_type=personnel&source_id=${encodeURIComponent(personnelId)}`]),
        tryFetchJson([`/api/documents?personnel_id=${encodeURIComponent(personnelId)}`]),
      ]);
      setDocumentSets((Array.isArray(sets)?sets:[]).filter(x=>x?.active!==false && ["personnel","both"].includes(String(x?.source_type||"personnel").toLowerCase())));
      setDocumentSetInstances(Array.isArray(instances)?instances:[]);
      setPersonnelDocuments(Array.isArray(docs)?docs:[]);
    } catch(e) { setDocumentSetsError(e?.message||"Documentsets konden niet worden geladen."); }
    finally { setDocumentSetsBusy(false); }
  }
  async function openPersonnelDocumentSets(){ if(!selectedId)return; setDocumentSetsOpen(true); await loadPersonnelDocumentSets(selectedId); }
  async function startPersonnelDocumentSet(documentSetId){
    try { setDocumentSetsBusy(true); setDocumentSetsError("");
      await tryFetchJson(["/api/document-set-instances"],{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({document_set_id:documentSetId,source_type:"personnel",source_id:String(selectedId),source_label:form.search_name||joinSp([form.first_name,form.name_prefix,form.last_name]),company_code_id:form.company_code_id?Number(form.company_code_id):null})});
      await loadPersonnelDocumentSets(selectedId);
    } catch(e){setDocumentSetsError(e?.message||"Documentset starten mislukt.");setDocumentSetsBusy(false);}
  }
  async function deletePersonnelDocumentSet(instance){
    if(!instance?.id || isReadonlyRole || documentSetsBusy) return;
    const setName=instance.document_set_name||"deze documentset";
    if(!window.confirm(`Gestarte documentset "${setName}" verwijderen?\n\nDe documentset wordt teruggedraaid en verdwijnt ook uit het Actiecentrum. De documenten zelf blijven behouden.`)) return;
    try {
      setDocumentSetsBusy(true);
      setDocumentSetsError("");
      await tryFetchJson([`/api/document-set-instances/${instance.id}`],{method:"DELETE"});
      await loadPersonnelDocumentSets(selectedId);
    } catch(e){
      setDocumentSetsError(e?.message||"Gestarte documentset verwijderen mislukt.");
      setDocumentSetsBusy(false);
    }
  }
  async function updateDocumentSetItem(item,patch={}){
    try { setDocumentSetsBusy(true); setDocumentSetsError("");
      await tryFetchJson([`/api/document-set-instance-items/${item.id}`],{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify({document_id:patch.document_id!==undefined?patch.document_id:item.document_id,document_received:patch.document_received!==undefined?patch.document_received:item.document_received===true,signature_received:patch.signature_received!==undefined?patch.signature_received:item.signature_received===true,note:item.note||""})});
      await loadPersonnelDocumentSets(selectedId);
    } catch(e){setDocumentSetsError(e?.message||"Documentset-onderdeel bijwerken mislukt.");setDocumentSetsBusy(false);}
  }

  async function uploadDocumentForSetItem(item, file) {
    if (!item?.id || !file || !selectedId || isReadonlyRole || documentSetsBusy) return;

    try {
      setDocumentSetsBusy(true);
      setDocumentSetsError("");

      const token = sessionStorage.getItem("token") || localStorage.getItem("token") || "";
      const tenant = sessionStorage.getItem("tenant") || localStorage.getItem("tenant") || "demo";
      let parsedUser = {};
      try {
        parsedUser = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "{}");
      } catch {}
      const companyCodeIds = sessionStorage.getItem("staffiq_company_code_ids") || localStorage.getItem("staffiq_company_code_ids") || "";

      const data = new FormData();
      data.append("file", file);
      data.append("personnel_id", String(selectedId));
      data.append("company_code_id", String(form.company_code_id || ""));
      data.append("document_type", item.document_type_name || item.title || "Personeel");
      if (item.document_type_id) data.append("document_type_id", String(item.document_type_id));
      data.append("source_module", "personnel");
      data.append("source_id", String(selectedId));
      data.append("description", `Documentset: ${item.title || item.document_type_name || "document"}`);

      const headers = {
        Authorization: token ? `Bearer ${token}` : undefined,
        "x-tenant": tenant,
        "x-role": parsedUser?.role || "user",
        "x-staffiq-user-id": parsedUser?.id || undefined,
        "x-staffiq-user-email": parsedUser?.email || undefined,
        "x-staffiq-user-name": parsedUser?.display_name || parsedUser?.name || undefined,
        "x-company-code-ids": companyCodeIds || undefined,
      };
      Object.keys(headers).forEach((k) => headers[k] === undefined && delete headers[k]);

      const res = await fetch(buildUrl("/api/documents/upload"), { method: "POST", headers, body: data });
      const saved = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(saved?.error || saved?.message || "Document uploaden mislukt.");

      // De upload-API retourneert { ok: true, document: {...} }.
      // Voor compatibiliteit accepteren we ook een direct id op rootniveau.
      const newDocumentId = Number(saved?.document?.id || saved?.id || 0);
      if (!newDocumentId) throw new Error("Document is opgeslagen, maar document-id ontbreekt.");

      await tryFetchJson([`/api/document-set-instance-items/${item.id}`], {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          document_id: newDocumentId,
          document_received: true,
          signature_received: item.signature_received === true,
          note: item.note || "",
        }),
      });

      await loadPersonnelDocumentSets(selectedId);
    } catch (e) {
      setDocumentSetsError(e?.message || "Document uploaden en koppelen mislukt.");
      setDocumentSetsBusy(false);
    }
  }

  function payloadFromForm() {
    return {
      company_code_id: form.company_code_id ? Number(form.company_code_id) : null,
      employment_type: employmentTypeOf(form),
      source_organisation_id: form.source_organisation_id ? Number(form.source_organisation_id) : null,

      first_name: form.first_name || "",
      initials: form.initials || "",
      name_prefix: form.name_prefix || "",
      last_name: form.last_name || "",
      gender: form.gender || "",
      birth_date: form.birth_date || null,
      salutation: form.salutation || "",
      attention_to: form.attention_to || "",
      search_name: form.search_name || "",

      status: form.status || "",
      job_functions: form.job_functions || "",
      marital_status: form.marital_status || "",
      birth_place: form.birth_place || "",
      birth_country: form.birth_country || "",
      nationality: form.nationality || "",

      employment_start_date: form.employment_start_date || null,
      employment_end_date: form.employment_end_date || null,
      hourly_wage: parseDecimal(form.hourly_wage),
      sales_rate: parseDecimal(form.sales_rate),
      monthly_wage: parseDecimal(form.monthly_wage),
      rate_level: form.rate_level || "",

      id_type: form.id_type || "",
      id_number: form.id_number || "",
      id_expiry_date: form.id_expiry_date || null,
      bsn: form.bsn || "",
      residence_permit: form.residence_permit || "",
      driving_license: form.driving_license || "",
      own_transport: !!form.own_transport,
      company_transport: !!form.company_transport,
      transport_description: form.transport_description || "",

      visit_street: form.visit_street || "",
      visit_house_number: form.visit_house_number || "",
      visit_addition: form.visit_addition || "",
      visit_postcode: (form.visit_postcode || "").toUpperCase().replace(/\s+/g, ""),
      visit_city: form.visit_city || "",
      visit_province: form.visit_province || "",
      visit_region: form.visit_region || "",
      visit_country: form.visit_country || "",

      corr_street: form.corr_street || "",
      corr_house_number: form.corr_house_number || "",
      corr_addition: form.corr_addition || "",
      corr_postcode: (form.corr_postcode || "").toUpperCase().replace(/\s+/g, ""),
      corr_city: form.corr_city || "",
      corr_province: form.corr_province || "",
      corr_region: form.corr_region || "",
      corr_country: form.corr_country || "",

      phone: form.phone || "",
      mobile: form.mobile || "",
      email: form.email || "",
      website: form.website || "",

      correspondence_whatsapp: !!form.correspondence_whatsapp,
      correspondence_email: !!form.correspondence_email,
      post_digital: !!form.post_digital,
      mailing_list: !!form.mailing_list,

      zzp: employmentTypeOf(form) === "ZZP",
      zzp_company_name: form.zzp_company_name || "",
      zzp_kvk: form.zzp_kvk || "",
      zzp_vat_number: form.zzp_vat_number || "",
      zzp_vat_rate: form.zzp_vat_rate || "",
      zzp_purchase_rate: parseDecimal(form.zzp_purchase_rate),
      zzp_sales_rate: parseDecimal(form.zzp_sales_rate),

      own_insurance: !!form.own_insurance,
      insurance_company: form.insurance_company || "",
      insurance_name: form.insurance_name || "",
      insurance_number: form.insurance_number || "",

      remarks: form.remarks || "",
      active: form.active !== false,
    };
  }

async function saveItem() {
    if (isFormLocked) return;

    // Directe guard via ref: voorkomt ook een tweede klik voordat React de
    // disabled-status van de knop opnieuw heeft gerenderd.
    if (saveInProgressRef.current) return;

    setSaveError("");
    setFlash("");

    if (!safeTrim(form.last_name) && !safeTrim(form.search_name)) {
      setSaveError("Vul minimaal achternaam of zoeknaam in.");
      return;
    }

    saveInProgressRef.current = true;
    setSaveBusy(true);

    try {
      const isUpdate = formHasId();
      const saved = await tryFetchJson(
        isUpdate
          ? [`/api/personnel/${form.id}`, `/api/personeel/${form.id}`]
          : ["/api/personnel", "/api/personeel"],
        {
          method: isUpdate ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payloadFromForm()),
        }
      );

      if (saved && typeof saved === "object") {
        setItems((prev) => {
          const id = saved.id;
          const idx = prev.findIndex((x) => String(x.id) === String(id));
          if (idx < 0) return [saved, ...prev];
          const copy = prev.slice();
          copy[idx] = { ...copy[idx], ...saved };
          return copy;
        });

        if (pendingCvMatchingRows.length && saved.id) {
          const createdMatchingRows = [];

          for (const row of pendingCvMatchingRows) {
            const created = await tryFetchJson(
              [`/api/personnel/${saved.id}/matching`],
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(row),
              }
            );

            if (created) createdMatchingRows.push(created);
          }

          if (createdMatchingRows.length) {
            setMatchingRows((prev) => [...prev, ...createdMatchingRows]);
          }

          setPendingCvMatchingRows([]);
        }

        if (pendingProfilePhotoData && saved.id) {
          try {
            const photoSaved = await tryFetchJson(
              [`/api/personnel/${saved.id}/photo`],
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ image_data: pendingProfilePhotoData }),
              }
            );

            setItems((prev) =>
              prev.map((x) =>
                String(x.id) === String(saved.id)
                  ? { ...x, profile_photo_path: photoSaved?.profile_photo_path || "pasfoto.png" }
                  : x
              )
            );
          } catch (photoErr) {
            console.log("Klaargezette pasfoto kon niet automatisch worden opgeslagen:", photoErr);
            setFlash("Personeel opgeslagen, maar pasfoto opslaan is mislukt.");
          }
        }
      }

      resetRightPanel();
      setMode("search");
      setReloadKey((k) => k + 1);
      setFlash("Opgeslagen ✅");
      setTimeout(() => setFlash(""), 900);

      positionSearchNoKeyboard();
    } catch (e) {
      setSaveError(e?.message || String(e));
    } finally {
      saveInProgressRef.current = false;
      setSaveBusy(false);
    }
  }

  async function deleteItem() {
    if (isFormLocked) return;
    if (!formHasId()) return;

    if (!window.confirm("Weet je zeker dat je deze medewerker wilt verwijderen?")) return;

    try {
      await tryFetchJson([`/api/personnel/${form.id}`, `/api/personeel/${form.id}`], {
        method: "DELETE",
      });

      setItems((prev) => prev.filter((x) => String(x.id) !== String(form.id)));
      resetRightPanel();
      setMode("search");
      setFlash("Verwijderd ✅");
      setTimeout(() => setFlash(""), 900);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

function startEditMatchingRow(row) {
    if (!row) return;

    setMatchingOpen(true);
    setMatchingEditId(row.id || null);
    setMatchingDraft({
      id: row.id || null,
      category: row.category || "",
      name: row.name || "",
      description: row.description || "",
      level: row.level || "",
      date_from: isoDate(row.date_from),
      date_to: isoDate(row.date_to),
      diploma: row.diploma === true,
      employer: row.employer || "",
    });

    positionMatchingFormAndFocusCategory();
  }

  function cancelEditMatchingRow() {
    setMatchingEditId(null);
    setMatchingDraft(emptyMatchingRow());
    positionRightPanelTopNoKeyboard();
  }

async function saveMatchingRow() {
    if (!formHasId()) {
      setSaveError("Sla de medewerker eerst op voordat je matchinggegevens toevoegt.");
      return;
    }

    const cleanDraft = {
      ...matchingDraft,
      category: safeTrim(matchingDraft.category),
      name: safeTrim(matchingDraft.name),
      description: safeTrim(matchingDraft.description),
      level: safeTrim(matchingDraft.level),
      employer: safeTrim(matchingDraft.employer),
      date_from: matchingDraft.date_from || "",
      date_to: matchingDraft.date_to || "",
      diploma: matchingDraft.diploma === true,
    };

    const hasAnyMatchingValue =
      safeTrim(cleanDraft.category) ||
      safeTrim(cleanDraft.name) ||
      safeTrim(cleanDraft.description) ||
      safeTrim(cleanDraft.level) ||
      safeTrim(cleanDraft.employer) ||
      safeTrim(cleanDraft.date_from) ||
      safeTrim(cleanDraft.date_to) ||
      cleanDraft.diploma === true;

    if (!hasAnyMatchingValue) {
      setSaveError("Vul minimaal één veld in bij Matching gegevens.");
      return;
    }

    if (!cleanDraft.category) {
      cleanDraft.category = "Algemeen";
    }

    try {
      setSaveError("");

      const isEdit = !!matchingEditId;

      const saved = await tryFetchJson(
        isEdit
          ? [`/api/personnel-matching/${matchingEditId}`]
          : [`/api/personnel/${form.id}/matching`],
        {
          method: isEdit ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(cleanDraft),
        }
      );

      if (isEdit) {
        setMatchingRows((prev) =>
          prev.map((x) =>
            String(x.id) === String(matchingEditId)
              ? { ...x, ...saved }
              : x
          )
        );
      } else {
        setMatchingRows((prev) => [...prev, saved]);
      }

      setMatchingEditId(null);
      setMatchingDraft(emptyMatchingRow());
      setMatchingOpen(true);

      setFlash(isEdit ? "Matchingregel gewijzigd ✅" : "Matchingregel toegevoegd ✅");
      setTimeout(() => setFlash(""), 900);

      positionMatchingBlockNoKeyboard();
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  async function deleteMatchingRow(id) {
    if (!id) return;
    if (!window.confirm("Matchingregel verwijderen?")) return;

    try {
      await tryFetchJson([`/api/personnel-matching/${id}`], { method: "DELETE" });
      setMatchingRows((prev) => prev.filter((x) => String(x.id) !== String(id)));

      if (String(matchingEditId || "") === String(id)) {
        setMatchingEditId(null);
        setMatchingDraft(emptyMatchingRow());
      }

      positionMatchingHeaderNoKeyboard();
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  function getFocusableElements() {
    return focusOrderRefs
      .map((r) => r?.current)
      .filter(Boolean)
      .filter((el) => {
        try {
          if (el.disabled) return false;
          const rect = el.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0;
        } catch {
          return false;
        }
      });
  }

  function focusNextField(dir = +1) {
    const els = getFocusableElements();
    if (!els.length) return;

    const active = document.activeElement;
    let idx = els.findIndex((el) => el === active);

    if (idx === -1) idx = dir > 0 ? -1 : els.length;

    let next = idx + dir;
    if (next < 0) next = 0;
    if (next > els.length - 1) next = els.length - 1;

    try {
      els[next]?.focus?.();
      els[next]?.scrollIntoView?.({ block: "nearest" });
    } catch {}
  }

  function moveCursor(delta) {
    setListIndex((prev) => {
      const L = sortedItems.length;
      if (L <= 0) return -1;
      const start = prev < 0 ? 0 : prev;
      const next = Math.max(0, Math.min(L - 1, start + delta));
      setTimeout(() => rowRefs.current[next]?.scrollIntoView?.({ block: "nearest" }), 0);
      return next;
    });
  }

  function openCurrentRow() {
    const idx = listIndex < 0 ? 0 : listIndex;
    const row = sortedItems[idx];
    if (row) loadItemFromRow(row);
  }

  const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 5000,
    onCommand: (cmd) => {
      if (!cmd?.type) return false;

      if (cmd.type === "next") {
        if (mode === "form") focusNextField(+1);
        else moveCursor(+1);
        return true;
      }

      if (cmd.type === "prev") {
        if (mode === "form") focusNextField(-1);
        else moveCursor(-1);
        return true;
      }

      if (cmd.type === "new") return newItem(), true;
      if (cmd.type === "refresh") return vernieuwList(), true;
      if (cmd.type === "save") return saveItem(), true;
      if (cmd.type === "delete") return deleteItem(), true;
      if (cmd.type === "select") return openCurrentRow(), true;

      return false;
    },
    onDictation: (text) => {
      const raw = String(text || "").trim();
      if (!raw) return;

      setSpeechInfo(`🎙 ${raw}`);
      setTimeout(() => setSpeechInfo(""), 2000);

      const t = norm(raw);
      if (t === "volgende") return mode === "form" ? focusNextField(+1) : moveCursor(+1);
      if (t === "vorige" || t === "terug") return mode === "form" ? focusNextField(-1) : moveCursor(-1);
      if (t === "nieuw") return newItem();
      if (t === "vernieuw" || t === "vernieuwen") return vernieuwList();
      if (t === "opslaan") return saveItem();
      if (t === "selecteer" || t === "item" || t === "rechts") return openCurrentRow();
      if (t === "lijst" || t === "links") return setMode("search");

      if (mode === "search") {
        if (isEmptyWord(raw)) setQ("");
        else setQ(raw);
      }
    },
  });

// ---------- LAYOUT ----------
  const isMobile = window.innerWidth <= 768;
  const RIGHT_WIDTH = isMobile ? "100%" : 320;
  const GAP = isMobile ? 18 : 14;
  const TABLE_MIN_WIDTH = 1340;
  const MID_BOTTOM_OFFSET = isMobile ? 90 : 135;

  const colWidths = {
    search: 120,
    status: 75,
    function: 95,
    pc: 70,
    city: 90,
    region: 70,
    phone: 80,
    mobile: 80,
    email: 105,
    employmentType: 82,
    wage: 65,
    active: 55,
    companyCode: 90,
  };

  const searchInputStyle = {
    padding: "7px 10px",
    width: isMobile ? "100%" : 220,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  };

  const inputStyle = {
    display: "block",
    padding: "7px 10px",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
  };

  const selectStyle = {
    display: "block",
    height: 30,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  };

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const buttonDisabledStyle = { ...buttonStyle, opacity: 0.55, cursor: "not-allowed" };

  const buttonPrimary = (bg) => ({
    ...buttonStyle,
    background: bg,
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  });

  const thBase = {
    textAlign: "left",
    fontSize: 12,
    color: "#444",
    fontWeight: 700,
    padding: "8px 6px",
    borderBottom: "1px solid #e6e6e6",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: "#fff",
    userSelect: "none",
  };

  const thClickable = { ...thBase, cursor: "pointer" };

  const filterThStyle = {
    ...thBase,
    fontWeight: 400,
    padding: "6px 6px 8px 6px",
    borderBottom: "1px solid #ededed",
    top: 32,
    zIndex: 4,
    cursor: "default",
  };

  const tdStyle = {
    padding: "7px 6px",
    borderBottom: "1px solid #efefef",
    fontSize: 13,
    color: "#222",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const rowBgCursor = "#f3f7ff";
  const rowBgSelected = "#e6f0ff";

  const labelStyle = { fontSize: 13, color: "#333" };
  const sectionTitleStyle = {
    fontSize: 13,
    fontWeight: 700,
    opacity: 0.75,
    marginTop: 8,
  };

  const photoFrameStyle = {
    width: 86,
    height: 112,
    border: "1px solid #d9d9d9",
    borderRadius: 8,
    background: "#f7f7f7",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    color: "#777",
    flexShrink: 0,
  };

  const photoCropMetrics = getPhotoCropMetrics();

  function filterSelect(value, setValue, options, includeYesNo = false) {
    return (
      <select value={value} onChange={(e) => setValue(e.target.value)} style={selectStyle}>
        <option value="">(alle)</option>
        {includeYesNo ? (
          <>
            <option value="j">j</option>
            <option value="n">n</option>
          </>
        ) : (
          options.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))
        )}
      </select>
    );
  }

function renderTextField(label, key, ref = null, type = "text", extra = {}) {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <input
          ref={ref}
          type={type}
          value={form[key] || ""}
          disabled={isFormLocked}
          onChange={(e) => {
            if (extra.onChange) {
              extra.onChange(e.target.value);
              return;
            }

            setForm({ ...form, [key]: e.target.value });
          }}
          style={inputStyle}
          placeholder={extra.placeholder || ""}
          list={extra.list || undefined}
        />
      </>
    );
  }

  function renderMoneyField(label, key) {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <input
          type="text"
          inputMode="decimal"
          value={form[key] || ""}
          disabled={isFormLocked}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          onBlur={() =>
            setForm((prev) => ({
              ...prev,
              [key]: moneyInput(prev[key]),
            }))
          }
          style={inputStyle}
        />
      </>
    );
  }

  function renderCheckbox(label, key) {
    return (
      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
        <input
          type="checkbox"
          checked={form[key] === true}
          disabled={isFormLocked}
          onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
        />
        {label}
      </label>
    );
  }

  return (
    <div>
      {saveBusy && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.30)",
            zIndex: 1000000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 10,
              padding: "18px 22px",
              boxShadow: "0 18px 50px rgba(0,0,0,0.35)",
              maxWidth: 380,
              width: "100%",
              textAlign: "center",
              fontFamily: "Arial, Helvetica, sans-serif",
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
              Personeel wordt opgeslagen...
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.5, color: "#333" }}>
              Gegevens, Matching-informatie en pasfoto worden verwerkt.
              <br />
              Dit kan enkele seconden duren.
            </div>
          </div>
        </div>
      )}

      {cvImportBusy && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 999999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 10,
              padding: "18px 22px",
              boxShadow: "0 18px 50px rgba(0,0,0,0.35)",
              maxWidth: 360,
              width: "100%",
              textAlign: "center",
              fontFamily: "Arial, Helvetica, sans-serif",
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
              AI is bezig...
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.5, color: "#333" }}>
              Het CV wordt gelezen en de personeelsgegevens worden alvast ingevuld.
              <br />
              Dit kan even duren.
            </div>
          </div>
        </div>
      )}

      {cvImportResultOpen && (cvImportMessage || cvImportError) && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.25)",
            zIndex: 999998,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 10,
              padding: "18px 22px",
              boxShadow: "0 18px 50px rgba(0,0,0,0.30)",
              maxWidth: 420,
              width: "100%",
              fontFamily: "Arial, Helvetica, sans-serif",
            }}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                marginBottom: 10,
                color: cvImportError ? "crimson" : "#1f6b1f",
              }}
            >
              {cvImportError ? "CV-analyse mislukt" : "CV geanalyseerd"}
            </div>

            <div style={{ fontSize: 14, lineHeight: 1.5, color: "#333", whiteSpace: "pre-wrap" }}>
              {cvImportError || cvImportMessage}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button
                type="button"
                onClick={() => setCvImportResultOpen(false)}
                style={buttonPrimary("#1f4e79")}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : `minmax(0, 1fr) ${RIGHT_WIDTH}px`,
          columnGap: GAP,
          rowGap: isMobile ? 22 : 0,
          alignItems: "start",
          minWidth: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            ref={pageTopRef}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 0,
              marginBottom: 10,
            }}
          >
            <h1 style={{ margin: 0 }}>
              Personeel <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
            </h1>

            <HelpButton
              topic="personeel"
              title="Help over Personeel"
            />
          </div>

{!isMobile ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "nowrap" }}>
              <input
                ref={searchRef}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setMode("search");
                }}
                placeholder="Zoek..."
                style={searchInputStyle}
              />

              <div
                title={`Aantal regels in het grid: ${gridCount}`}
                style={{
                  minWidth: 52,
                  height: 32,
                  padding: "0 10px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 4,
                  background: "#f7fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#1f4e79",
                  boxSizing: "border-box",
                  whiteSpace: "nowrap",
                }}
              >
                {gridCount}
              </div>

              <button
                onMouseDown={(e) => e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                onClick={() => voice.startOnce?.()}
                disabled={!voice.supported || voice.listening}
                style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
              >
                {voice.listening ? "🎤 Luistert..." : "🎤 Spreek"}
              </button>

              <button onClick={newItem} disabled={isReadonlyRole} style={isReadonlyRole ? buttonDisabledStyle : buttonStyle}>
                ➕ Nieuw
              </button>

              <button onClick={vernieuwList} style={buttonStyle}>
                🔄 Vernieuw
              </button>

              <button
                onClick={() => setDossierOpen(true)}
                disabled={!selectedId}
                style={!selectedId ? buttonDisabledStyle : buttonStyle}
                title={!selectedId ? "Selecteer eerst een personeelslid" : "Open het complete personeelsdossier"}
              >
                📁 Dossier
              </button>

            </div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) 58px",
                  gap: 6,
                  marginBottom: 8,
                  alignItems: "center",
                }}
              >
                <input
                  ref={searchRef}
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setMode("search");
                  }}
                  placeholder="Zoek..."
                  style={{
                    ...searchInputStyle,
                    width: "100%",
                    minWidth: 0,
                  }}
                />

                <div
                  title={`Aantal regels in het grid: ${gridCount}`}
                  style={{
                    height: 34,
                    minHeight: 34,
                    padding: "0 8px",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    background: "#f7fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#1f4e79",
                    boxSizing: "border-box",
                    whiteSpace: "nowrap",
                    lineHeight: "1",
                  }}
                >
                  {gridCount}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 6, marginTop: 8 }}>
                <button
                  onClick={() => voice.startOnce?.()}
                  disabled={!voice.supported || voice.listening}
                  style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
                >
                  {voice.listening ? "🎤..." : "🎤 Spreek"}
                </button>

                <button onClick={newItem} disabled={isReadonlyRole} style={isReadonlyRole ? buttonDisabledStyle : buttonStyle}>
                  ➕ Nieuw
                </button>

                <button onClick={vernieuwList} style={buttonStyle}>
                  🔄 Vernieuw
                </button>

                <button
                  onClick={() => setDossierOpen(true)}
                  disabled={!selectedId}
                  style={!selectedId ? buttonDisabledStyle : buttonStyle}
                  title={!selectedId ? "Selecteer eerst een personeelslid" : "Open het complete personeelsdossier"}
                >
                  📁 Dossier
                </button>

              </div>
            </>
          )}

          <div style={{ marginTop: 10 }}>
            {loading && <div>Laden...</div>}
            {error && <div style={{ color: "crimson" }}>{error}</div>}
            {saveError && <div style={{ marginBottom: 8, color: "crimson" }}>{saveError}</div>}
            {flash && <div style={{ marginBottom: 8, color: "green" }}>{flash}</div>}
            {speechInfo && <div style={{ opacity: 0.75 }}>{speechInfo}</div>}
            {voice.error && voice.error !== "no-speech" && (
              <div style={{ color: "crimson" }}>Spraakfout: {voice.error}</div>
            )}
          </div>

          {!loading && !error && (
            <div style={{ marginTop: 8, minWidth: 0 }}>
              <div
                ref={midScrollRef}
                style={{
                  overflowX: "scroll",
                  overflowY: "auto",
                  maxHeight: `calc(100vh - ${MID_BOTTOM_OFFSET}px)`,
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  background: "#fff",
                }}
              >
                <div style={{ minWidth: TABLE_MIN_WIDTH }}>
                  <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed" }}>
                    <colgroup>
                      <col style={{ width: colWidths.search }} />
                      <col style={{ width: colWidths.status }} />
                      <col style={{ width: colWidths.function }} />
                      <col style={{ width: colWidths.pc }} />
                      <col style={{ width: colWidths.city }} />
                      <col style={{ width: colWidths.region }} />
                      <col style={{ width: colWidths.phone }} />
                      <col style={{ width: colWidths.mobile }} />
                      <col style={{ width: colWidths.email }} />
                      <col style={{ width: colWidths.employmentType }} />
                      <col style={{ width: colWidths.wage }} />
                      <col style={{ width: colWidths.active }} />
                      <col style={{ width: colWidths.companyCode }} />
                    </colgroup>

                    <thead>
                      <tr>
                        <th style={thClickable} onClick={() => sortToggle("search")}>Zoeknaam{sortArrow("search")}</th>
                        <th style={thClickable} onClick={() => sortToggle("status")}>Status{sortArrow("status")}</th>
                        <th style={thClickable} onClick={() => sortToggle("function")}>Functie{sortArrow("function")}</th>
                        <th style={thClickable} onClick={() => sortToggle("pc")}>Postcode{sortArrow("pc")}</th>
                        <th style={thClickable} onClick={() => sortToggle("city")}>Plaats{sortArrow("city")}</th>
                        <th style={thClickable} onClick={() => sortToggle("region")}>Regio{sortArrow("region")}</th>
                        <th style={thClickable} onClick={() => sortToggle("phone")}>Telefoon{sortArrow("phone")}</th>
                        <th style={thClickable} onClick={() => sortToggle("mobile")}>Mobiel{sortArrow("mobile")}</th>
                        <th style={thClickable} onClick={() => sortToggle("email")}>E-mail{sortArrow("email")}</th>
                        <th style={thClickable} onClick={() => sortToggle("employment_type")}>Inzetvorm{sortArrow("employment_type")}</th>
                        <th style={thClickable} onClick={() => sortToggle("wage")}>Uurloon/tarief{sortArrow("wage")}</th>
                        <th style={thClickable} onClick={() => sortToggle("active")}>Actief{sortArrow("active")}</th>
                        <th style={thClickable} onClick={() => sortToggle("company_code")}>Bedrijfscode{sortArrow("company_code")}</th>
                      </tr>

                      <tr>
                        <th style={filterThStyle}>{filterSelect(searchNameFilter, setSearchNameFilter, searchNameOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(statusFilter, setStatusFilter, statusOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(functionFilter, setFunctionFilter, functionOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(pcFilter, setPcFilter, pcOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(cityFilter, setCityFilter, cityOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(regionFilter, setRegionFilter, regionOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(phoneFilter, setPhoneFilter, phoneOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(mobileFilter, setMobileFilter, mobileOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(emailFilter, setEmailFilter, emailOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(employmentTypeFilter, setEmploymentTypeFilter, employmentTypeGridOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(wageFilter, setWageFilter, wageOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(activeFilter, setActiveFilter, [], true)}</th>
                        <th style={filterThStyle}>{filterSelect(companyCodeFilter, setCompanyCodeFilter, companyCodeOptions)}</th>
                      </tr>
                    </thead>

                    <tbody>
                      {sortedItems.map((it, idx) => {
                        const isCursor = idx === listIndex;
                        const isSelected = String(selectedId || "") === String(it.id || "");

                        let bg = "transparent";
                        if (isSelected) bg = rowBgSelected;
                        else if (isCursor) bg = rowBgCursor;

                        return (
                          <tr
                            key={it.id}
                            ref={(el) => (rowRefs.current[idx] = el)}
                            onClick={() => {
                              setListIndex(idx);
                              loadItemFromRow(it);
                            }}
                            style={{ cursor: "pointer", backgroundColor: bg }}
                          >
                            <td style={tdStyle} title={it.search_name || ""}>{it.search_name || "-"}</td>
                            <td style={tdStyle}>{it.status || "-"}</td>
                            <td style={tdStyle} title={it.job_functions || ""}>{it.job_functions || "-"}</td>
                            <td style={tdStyle}>{it.corr_postcode || "-"}</td>
                            <td style={tdStyle}>{it.corr_city || "-"}</td>
                            <td style={tdStyle}>{it.corr_region || "-"}</td>
                            <td style={tdStyle} title={it.phone || ""}>
                              {it.phone ? (
                                <button
                                  type="button"
                                  onClick={(e) => handlePhoneGridClick(e, it.phone)}
                                  style={{
                                    border: "none",
                                    background: "transparent",
                                    padding: 0,
                                    margin: 0,
                                    color: "#1f4e79",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    font: "inherit",
                                  }}
                                  title={`Bellen: ${it.phone}`}
                                >
                                  {it.phone}
                                </button>
                              ) : (
                                "-"
                              )}
                            </td>

                            <td style={tdStyle} title={it.mobile || ""}>
                              {it.mobile ? (
                                <button
                                  type="button"
                                  onClick={(e) => handleMobileGridClick(e, it.mobile)}
                                  style={{
                                    border: "none",
                                    background: "transparent",
                                    padding: 0,
                                    margin: 0,
                                    color: "#1f4e79",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    font: "inherit",
                                  }}
                                  title={`Bellen / SMS / WhatsApp: ${it.mobile}`}
                                >
                                  {it.mobile}
                                </button>
                              ) : (
                                "-"
                              )}
                            </td>

                            <td style={tdStyle} title={it.email || ""}>
                              {it.email ? (
                                <button
                                  type="button"
                                  onClick={(e) => handleEmailGridClick(e, it.email)}
                                  style={{
                                    border: "none",
                                    background: "transparent",
                                    padding: 0,
                                    margin: 0,
                                    color: "#1f4e79",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    font: "inherit",
                                  }}
                                  title={`E-mailen: ${it.email}`}
                                >
                                  {it.email}
                                </button>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td style={tdStyle}>{employmentTypeOf(it)}</td>
                            <td style={tdStyle}>
                              {["ZZP", "Doorleen"].includes(employmentTypeOf(it))
                                ? (Number(it.zzp_purchase_rate || 0) ? formatMoney(it.zzp_purchase_rate) : "-")
                                : (Number(it.hourly_wage || 0) ? formatMoney(it.hourly_wage) : "-")}
                            </td>
                            <td
                              style={{ ...tdStyle, textAlign: "center" }}
                              title={parseActiveValue(it) === false ? "inactief" : "actief"}
                            >
                              <input
                                type="checkbox"
                                checked={parseActiveValue(it) !== false}
                                readOnly
                              />
                            </td>
                            <td style={tdStyle} title={companyCodeLabel(it)}>{companyCodeLabel(it) || "-"}</td>
                          </tr>
                        );
                      })}

                      {sortedItems.length === 0 && (
                        <tr>
                          <td colSpan={13} style={{ padding: 12, opacity: 0.7 }}>
                            Geen resultaten.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <div
          ref={rightScrollRef}
          style={{
            width: isMobile ? "100%" : RIGHT_WIDTH,
            minWidth: isMobile ? 0 : RIGHT_WIDTH,
            maxWidth: isMobile ? "100%" : RIGHT_WIDTH,
            position: isMobile ? "relative" : "sticky",
            top: isMobile ? "auto" : 0,
            alignSelf: "start",
            maxHeight: isMobile ? "none" : "calc(100vh - 40px)",
            overflowY: isMobile ? "visible" : "auto",
            background: "linear-gradient(180deg, #0b4f7a 0%, #083b5c 100%)",
            borderRadius: 10,
            padding: 10,
            paddingBottom: isMobile ? 34 : 10,
            boxSizing: "border-box",
            marginTop: isMobile ? 6 : 0,
            marginBottom: isMobile ? 34 : 0,
            paddingTop: isMobile ? 6 : 10,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 10,
              padding: 12,
              paddingBottom: isMobile ? 28 : 12,
              boxSizing: "border-box",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflow: isMobile ? "visible" : "hidden",
            }}
          >

<div
              style={{
                position: "relative",
                minHeight: remarkEditor ? 42 : 130,
                marginBottom: 4,
                paddingRight: remarkEditor ? 0 : 100,
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                }}
              >
                <h3 style={{ marginTop: 0, marginBottom: 0 }}>
                  Personeel
                </h3>

                {remarkEditor && (
                  <button
                    type="button"
                    onClick={closeRemarkEditorToSmall}
                    style={buttonStyle}
                    title="Terug naar normaal formulier"
                  >
                    ↩ Terug
                  </button>
                )}
              </div>

              {!remarkEditor && (
                <div
                  style={{
                    marginTop: 8,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    gap: 8,
                    width: 132,
                    maxWidth: 132,
                    height: 82,
                  }}
                >
                  <input
                    ref={cvFileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.txt,.rtf"
                    style={{ display: "none" }}
                    onChange={handleCvImportFileChange}
                  />

                  <button
                    type="button"
                    onClick={() => cvFileInputRef.current?.click?.()}
                    disabled={isFormLocked || cvImportBusy}
                    style={{
                      ...buttonStyle,
                      padding: "7px 8px",
                      fontSize: 14,
                      height: 32,
                      width: "100%",
                      background: "white",
                      color: "#1f4e79",
                      opacity: isFormLocked || cvImportBusy ? 0.5 : 1,
                      cursor: isFormLocked || cvImportBusy ? "not-allowed" : "pointer",
                    }}
                    title="Kies een PDF, Word-bestand of tekstbestand van een CV. De AI vult alleen alvast gegevens in; controleer alles voordat je opslaat."
                  >
                    📄 CV importeren
                  </button>
                </div>
              )}

              {!remarkEditor && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    display: "grid",
                    gap: 6,
                    justifyItems: "center",
                  }}
                >
                  <div style={photoFrameStyle}>
                    {pendingProfilePhotoData ? (
                      <img
                        src={pendingProfilePhotoData}
                        alt="Klaargezette pasfoto"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : formHasId() && form.profile_photo_path ? (
                      <img
                        src={photoUrl()}
                        alt="Pasfoto"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : (
                      <span>Pasfoto</span>
                    )}
                  </div>

                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*,.jpg,.jpeg,.png,.bmp,.webp"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      e.target.value = "";
                      handlePhotoFile(file);
                    }}
                  />

                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      type="button"
                      onClick={openPhotoPicker}
                      disabled={isFormLocked}
                      style={{
                        ...buttonStyle,
                        padding: "4px 7px",
                        fontSize: 11,
                        opacity: isFormLocked ? 0.5 : 1,
                        cursor: isFormLocked ? "not-allowed" : "pointer",
                      }}
                      title={formHasId() ? "Pasfoto kiezen" : "Pasfoto alvast klaarzetten. Deze wordt opgeslagen na Opslaan."}
                    >
                      Foto
                    </button>

                    {(pendingProfilePhotoData || (formHasId() && form.profile_photo_path)) && (
                      <button
                        type="button"
                        onClick={deletePhoto}
                        disabled={isFormLocked}
                        style={{
                          ...buttonStyle,
                          padding: "4px 7px",
                          fontSize: 11,
                          color: "#b00020",
                          opacity: isFormLocked ? 0.5 : 1,
                        }}
                        title="Pasfoto verwijderen"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {photoError && (
              <div style={{ color: "#b00020", fontSize: 12, marginBottom: 8 }}>
                {photoError}
              </div>
            )}

            {!remarkEditor && (
              <>
                <label style={labelStyle}>Bedrijfscode</label>
                <select
                  value={form.company_code_id || ""}
                  disabled={isFormLocked}
                  onChange={(e) => setForm({ ...form, company_code_id: e.target.value })}
                  style={selectStyle}
                >
                  {selectedCompanyCodesForForm(companyCodes).map((cc) => (
                    <option key={cc.id} value={cc.id}>
                      {cc.name || cc.code}
                    </option>
                  ))}
                </select>
              </>
            )}

            <datalist id="person_salutation_list">
              <option value="Geachte" />
              <option value="Beste" />
            </datalist>

            <datalist id="person_attention_list">
              <option value="De heer" />
              <option value="Mevrouw" />
              <option value="T.a.v." />
            </datalist>

            <datalist id="person_status_list">
              {statusOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_functions_list">
              {functionOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_marital_status_list">
              {maritalStatusOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_birth_place_list">
              {birthPlaceOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_birth_country_list">
              {birthCountryOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_nationality_list">
              {nationalityOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_id_type_list">
              {idTypeOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_residence_permit_list">
              {residencePermitOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_driving_license_list">
              {drivingLicenseOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_visit_street_list">
              {visitStreetOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_visit_city_list">
              {visitCityOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_visit_province_list">
              {visitProvinceOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_visit_region_list">
              {visitRegionOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_visit_country_list">
              {visitCountryOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_corr_street_list">
              {corrStreetOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_corr_city_list">
              {cityOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_corr_province_list">
              {corrProvinceOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_corr_region_list">
              {regionOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_corr_country_list">
              {corrCountryOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_zzp_vat_rate_list">
              {zzpVatRateOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_insurance_company_list">
              {insuranceCompanyOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_insurance_name_list">
              {insuranceNameOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            <datalist id="person_matching_category_list">
              {matchingCategoryOptions.map((x) => <option key={x} value={x} />)}
            </datalist>

            {remarkEditor && (
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ fontSize: 13, opacity: 0.8 }}>
                  Opmerking-editor (spreek: door-dicteren) — zeg <b>punt</b> voor “.”.
                </div>

                <textarea
                  ref={remarkBigRef}
                  value={form.remarks || ""}
                  disabled={isFormLocked}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  rows={15}
                  style={{
                    padding: "10px 10px",
                    width: "100%",
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                    background: "white",
                    resize: "vertical",
                    boxSizing: "border-box",
                    minHeight: "60vh",
                    fontSize: 14,
                    lineHeight: 1.4,
                  }}
                />

                <div
                  style={{
                    background: "white",
                    paddingTop: 10,
                    marginTop: 6,
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                    borderTop: "1px solid #eee",
                  }}
                >
                  <button
                    onClick={saveItem}
                    disabled={isFormLocked || saveBusy}
                    style={
                      isFormLocked || saveBusy
                        ? { ...buttonPrimary("#2f7dd1"), opacity: 0.5, cursor: "not-allowed" }
                        : buttonPrimary("#2f7dd1")
                    }
                  >
                    {saveBusy ? "Opslaan..." : "💾 Opslaan"}
                  </button>

                  <button
                    onClick={deleteItem}
                    disabled={isFormLocked || !formHasId()}
                    style={
                      isFormLocked || !formHasId()
                        ? { ...buttonPrimary("#d64545"), opacity: 0.5, cursor: "not-allowed" }
                        : buttonPrimary("#d64545")
                    }
                  >
                    🗑 Verwijderen
                  </button>
                </div>
              </div>
            )}

            {photoEditorOpen && (
              <div
                style={{
                  border: "1px solid #d9d9d9",
                  borderRadius: 8,
                  padding: 10,
                  marginBottom: 12,
                  background: "#fbfbfb",
                  display: "grid",
                  gap: 10,
                }}
              >
                <div style={{ fontWeight: 700 }}>
                  Pasfoto knippen / schalen
                </div>

                <div
                  style={{
                    width: PHOTO_CROP_BOX_W,
                    height: PHOTO_CROP_BOX_H,
                    maxWidth: "100%",
                    margin: "0 auto",
                    border: "2px solid #0b4f7a",
                    background: "#eee",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  {photoSrc && (
                    <img
                      ref={photoCropImageRef}
                      src={photoSrc}
                      alt="Te knippen foto"
                      onLoad={(e) => {
                        setPhotoNatural({
                          width: e.currentTarget.naturalWidth || 0,
                          height: e.currentTarget.naturalHeight || 0,
                        });
                      }}
                      style={{
                        position: "absolute",
                        left: photoCropMetrics.drawXInBox,
                        top: photoCropMetrics.drawYInBox,
                        width: photoCropMetrics.drawWInBox,
                        height: photoCropMetrics.drawHInBox,
                        maxWidth: "none",
                        transform: "none",
                        transformOrigin: "center center",
                        userSelect: "none",
                        pointerEvents: "none",
                      }}
                    />
                  )}

                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      border: "1px dashed rgba(255,255,255,0.8)",
                      boxSizing: "border-box",
                      pointerEvents: "none",
                    }}
                  />
                </div>

                <div style={{ display: "grid", gap: 6 }}>
                  <label style={labelStyle}>Zoom</label>
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.01"
                    value={photoZoom}
                    onChange={(e) => setPhotoZoom(Number(e.target.value))}
                  />

                  <label style={labelStyle}>Horizontaal schuiven</label>
                  <input
                    type="range"
                    min="-160"
                    max="160"
                    step="1"
                    value={photoOffsetX}
                    onChange={(e) => setPhotoOffsetX(Number(e.target.value))}
                  />

                  <label style={labelStyle}>Verticaal schuiven</label>
                  <input
                    type="range"
                    min="-220"
                    max="220"
                    step="1"
                    value={photoOffsetY}
                    onChange={(e) => setPhotoOffsetY(Number(e.target.value))}
                  />
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={saveCroppedPhoto}
                    disabled={photoSaving}
                    style={buttonPrimary("#2f7dd1")}
                  >
                    {photoSaving ? "Opslaan..." : "💾 Pasfoto opslaan"}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setPhotoEditorOpen(false);
                      setPhotoSrc("");
                      setPhotoNatural({ width: 0, height: 0 });
                      setPhotoError("");
                    }}
                    style={buttonStyle}
                  >
                    Annuleren
                  </button>
                </div>

                <div style={{ fontSize: 12, opacity: 0.7 }}>
                  Tip: gebruik Zoom en schuiven totdat het hoofd netjes in het kader staat.
                </div>
              </div>
            )}

            <div
              style={{
                display: remarkEditor ? "none" : "grid",
                gridTemplateColumns: "minmax(0, 1fr)",
                gap: 8,
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                overflow: "hidden",
                justifyItems: "stretch",
                alignItems: "start",
              }}
            >
              <div style={sectionTitleStyle}>Persoon</div>

              {renderTextField("Voornaam", "first_name", firstNameRef)}
              {renderTextField("Voorletters", "initials", initialsRef)}
              {renderTextField("Tussenvoegsel(s)", "name_prefix", namePrefixRef)}
              {renderTextField("Achternaam", "last_name", lastNameRef)}

              <label style={labelStyle}>Geslacht (m/v/o)</label>
              <select
                ref={genderRef}
                value={form.gender || ""}
                disabled={isFormLocked}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
                style={selectStyle}
              >
                <option value="">(geen)</option>
                <option value="m">m</option>
                <option value="v">v</option>
                <option value="o">o</option>
              </select>

{renderTextField("Geboortedatum", "birth_date", birthDateRef, "date")}
              {renderTextField("Aanhef", "salutation", salutationRef, "text", {
                placeholder: "Bijv. Geachte of Beste",
                list: "person_salutation_list",
              })}
              {renderTextField("Aanschrijving", "attention_to", attentionToRef, "text", {
                placeholder: "Bijv. De heer, Mevrouw of T.a.v.",
                list: "person_attention_list",
              })}
              {renderTextField("Zoeknaam", "search_name", searchNameRef)}

              <hr style={{ margin: "10px 0" }} />

<div style={sectionTitleStyle}>HRM</div>

              <label style={labelStyle}>Inzetvorm</label>
              <select
                value={form.employment_type || "Vast"}
                disabled={isFormLocked}
                onChange={(e) => {
                  const nextType = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    employment_type: nextType,
                    zzp: nextType === "ZZP",
                  }));
                }}
                style={selectStyle}
              >
                {employmentTypeOptions.map((x) => (
                  <option key={x} value={x}>{x}</option>
                ))}
              </select>

              {renderTextField("Status", "status", statusRef, "text", { list: "person_status_list" })}
              {renderTextField("Functies", "job_functions", functionsRef, "text", { list: "person_functions_list" })}
              {renderTextField("Burgerlijke staat", "marital_status", maritalRef, "text", { list: "person_marital_status_list" })}
              {renderTextField("Geboorteplaats", "birth_place", null, "text", { list: "person_birth_place_list" })}
              {renderTextField("Geboorteland", "birth_country", null, "text", { list: "person_birth_country_list" })}
              {renderTextField("Nationaliteit", "nationality", null, "text", { list: "person_nationality_list" })}

              {isPayrollEmployment && (
                <>
                  {renderTextField("Datum in dienst", "employment_start_date", null, "date")}
                  {renderTextField("Datum uit dienst", "employment_end_date", null, "date")}
                  {renderMoneyField("Uurloon", "hourly_wage")}
                  {renderMoneyField("Verkooptarief", "sales_rate")}
                  {renderMoneyField("Maandloon", "monthly_wage")}
                </>
              )}

              {isExternalEmployment && (
                <>
                  <div style={{ ...sectionTitleStyle, marginTop: 12 }}>
                    {form.employment_type === "Doorleen" ? "Doorleen gegevens" : "ZZP gegevens"}
                  </div>

                  <label style={labelStyle}>Bedrijfsnaam</label>
                  <input
                    type="text"
                    value={form.zzp_company_name || ""}
                    disabled={isFormLocked}
                    onChange={(e) => handleExternalCompanyNameChange(e.target.value)}
                    style={inputStyle}
                    list="person_external_company_list"
                    autoComplete="off"
                    placeholder="Kies een actieve relatie of vul vrij in"
                  />
                  <datalist id="person_external_company_list">
                    {organisations
                      .slice()
                      .sort((a, b) =>
                        organisationDisplayName(a).localeCompare(
                          organisationDisplayName(b),
                          "nl"
                        )
                      )
                      .map((o) => (
                        <option
                          key={o.id}
                          value={organisationDisplayName(o)}
                        />
                      ))}
                  </datalist>

                  {renderTextField("KvK", "zzp_kvk")}
                  {renderTextField("BTW nummer", "zzp_vat_number")}
                  {renderTextField("BTW tarief", "zzp_vat_rate", null, "text", { list: "person_zzp_vat_rate_list" })}
                  {renderMoneyField("Inkooptarief", "zzp_purchase_rate")}
                  {renderMoneyField("Verkooptarief", "zzp_sales_rate")}
                </>
              )}

              <label style={labelStyle}>Ervaring</label>
              <select
                value={form.rate_level || ""}
                disabled={isFormLocked}
                onChange={(e) => setForm({ ...form, rate_level: e.target.value })}
                style={selectStyle}
              >
                <option value="">(geen)</option>
                <option value="Junior">Junior</option>
                <option value="Medior">Medior</option>
                <option value="Senior">Senior</option>
              </select>

              <hr style={{ margin: "10px 0" }} />

<div style={sectionTitleStyle}>ID / documenten</div>
              {renderTextField("Type ID", "id_type", null, "text", { list: "person_id_type_list" })}
              {renderTextField("Nummer", "id_number")}
              {renderTextField("Verloopdatum", "id_expiry_date", null, "date")}
              {renderTextField("BSN", "bsn")}
              {renderTextField("Verblijfsvergunning", "residence_permit", null, "text", { list: "person_residence_permit_list" })}
              {renderTextField("Rijbewijs", "driving_license", null, "text", { list: "person_driving_license_list" })}

              <div style={{ marginTop: 4, marginBottom: 4 }}>
                <div style={{ ...labelStyle, marginBottom: 5 }}>Vervoer</div>
                <div style={{ display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" }}>
                  <label style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={form.own_transport === true}
                      disabled={isFormLocked}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          own_transport: e.target.checked,
                          company_transport: e.target.checked ? false : prev.company_transport,
                        }))
                      }
                    />
                    Eigen
                  </label>

                  <label style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={form.company_transport === true}
                      disabled={isFormLocked}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          company_transport: e.target.checked,
                          own_transport: e.target.checked ? false : prev.own_transport,
                        }))
                      }
                    />
                    Vd Zaak
                  </label>
                </div>
              </div>

              {renderTextField("Omschrijving vervoer", "transport_description")}

              <hr style={{ margin: "10px 0" }} />

              <div style={sectionTitleStyle}>Contact</div>
              {renderTextField("Telefoon", "phone", phoneRef)}
              {renderTextField("Mobiel", "mobile", mobileRef)}
              {renderTextField("E-mail", "email", emailRef)}
              {renderTextField("Internet adres", "website")}

              <label
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!form.correspondence_whatsapp}
                  disabled={isFormLocked}
                  onChange={(e) => setForm({ ...form, correspondence_whatsapp: e.target.checked })}
                />
                Correspondentie en akkoord via WhatsApp (j/n)
              </label>

              <label
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!form.correspondence_email}
                  disabled={isFormLocked}
                  onChange={(e) => setForm({ ...form, correspondence_email: e.target.checked })}
                />
                Correspondentie en akkoord via E-mail (j/n)
              </label>

              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={!!form.post_digital}
                  disabled={isFormLocked}
                  onChange={(e) => setForm({ ...form, post_digital: e.target.checked })}
                />
                Ontvangt post digitaal (j/n)
              </label>

              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={!!form.mailing_list}
                  disabled={isFormLocked}
                  onChange={(e) => setForm({ ...form, mailing_list: e.target.checked })}
                />
                Mailinglijst (j/n)
              </label>

              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={!!form.active}
                  disabled={isFormLocked}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />
                Actief (j/n)
              </label>

              <hr style={{ margin: "10px 0" }} />

<div style={sectionTitleStyle}>Bezoekadres</div>
              {renderTextField("Straat", "visit_street", null, "text", { list: "person_visit_street_list" })}
              {renderTextField("Huisnummer", "visit_house_number")}
              {renderTextField("Toevoeging", "visit_addition")}
              {renderTextField("Postcode", "visit_postcode")}
              {renderTextField("Plaats", "visit_city", null, "text", { list: "person_visit_city_list" })}
              {renderTextField("Provincie", "visit_province", null, "text", { list: "person_visit_province_list" })}
              {renderTextField("Regio", "visit_region", null, "text", { list: "person_visit_region_list" })}
              {renderTextField("Land", "visit_country", null, "text", { list: "person_visit_country_list" })}

              <hr style={{ margin: "10px 0" }} />

              <div style={sectionTitleStyle}>Correspondentieadres</div>
              {renderTextField("Straat", "corr_street", null, "text", {
                list: "person_corr_street_list",
                onChange: (value) => {
                  setTouchedCorr((p) => ({ ...p, corr_street: true }));
                  setForm((p) => ({ ...p, corr_street: value }));
                },
              })}
              {renderTextField("Huisnummer", "corr_house_number", null, "text", {
                onChange: (value) => {
                  setTouchedCorr((p) => ({ ...p, corr_house_number: true }));
                  setForm((p) => ({ ...p, corr_house_number: value }));
                },
              })}
              {renderTextField("Toevoeging", "corr_addition", null, "text", {
                onChange: (value) => {
                  setTouchedCorr((p) => ({ ...p, corr_addition: true }));
                  setForm((p) => ({ ...p, corr_addition: value }));
                },
              })}
              {renderTextField("Postcode", "corr_postcode", null, "text", {
                onChange: (value) => {
                  setTouchedCorr((p) => ({ ...p, corr_postcode: true }));
                  setForm((p) => ({ ...p, corr_postcode: value }));
                },
              })}
              {renderTextField("Plaats", "corr_city", null, "text", {
                list: "person_corr_city_list",
                onChange: (value) => {
                  setTouchedCorr((p) => ({ ...p, corr_city: true }));
                  setForm((p) => ({ ...p, corr_city: value }));
                },
              })}
              {renderTextField("Provincie", "corr_province", null, "text", {
                list: "person_corr_province_list",
                onChange: (value) => {
                  setTouchedCorr((p) => ({ ...p, corr_province: true }));
                  setForm((p) => ({ ...p, corr_province: value }));
                },
              })}
              {renderTextField("Regio", "corr_region", null, "text", {
                list: "person_corr_region_list",
                onChange: (value) => {
                  setTouchedCorr((p) => ({ ...p, corr_region: true }));
                  setForm((p) => ({ ...p, corr_region: value }));
                },
              })}
              {renderTextField("Land", "corr_country", null, "text", {
                list: "person_corr_country_list",
                onChange: (value) => {
                  setTouchedCorr((p) => ({ ...p, corr_country: true }));
                  setForm((p) => ({ ...p, corr_country: value }));
                },
              })}

              <hr style={{ margin: "10px 0" }} />

              <div style={sectionTitleStyle}>Verzekering</div>
              {renderCheckbox("Eigen verzekering", "own_insurance")}
              {renderTextField("Maatschappij", "insurance_company", null, "text", { list: "person_insurance_company_list" })}
              {renderTextField("Verzekering", "insurance_name", null, "text", { list: "person_insurance_name_list" })}
              {renderTextField("Nummer", "insurance_number")}

              <hr style={{ margin: "10px 0" }} />

<div style={sectionTitleStyle}>Overig</div>

              <label style={{ fontSize: 13, color: "#333" }}>Opmerking</label>
              <textarea
                ref={remarkSmallRef}
                value={form.remarks || ""}
                disabled={isFormLocked}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                rows={6}
                style={{ ...inputStyle, resize: "vertical" }}
                onFocus={() => {
                  if (ignoreNextRemarkSmallFocusRef.current) {
                    ignoreNextRemarkSmallFocusRef.current = false;
                    return;
                  }

                  if (!isFormLocked) {
                    openRemarkEditorAndFocusEnd();
                  }
                }}
              />

<div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                  marginTop: 8,
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <button
                  onClick={openMatchingBlock}
                  disabled={!formHasId()}
                  style={{
                    ...(!formHasId()
                      ? { ...buttonPrimary("#198754"), opacity: 0.5, cursor: "not-allowed" }
                      : buttonPrimary("#198754")),
                    width: "100%",
                    minWidth: 0,
                    height: 32,
                    padding: "7px 8px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  🧩 Matching
                </button>

                <button
                  onClick={openPersonnelDocumentSets}
                  disabled={!formHasId()}
                  style={{
                    ...(!formHasId()
                      ? { ...buttonPrimary("#c58b1a"), opacity: 0.5, cursor: "not-allowed" }
                      : buttonPrimary("#c58b1a")),
                    width: "100%",
                    minWidth: 0,
                    height: 32,
                    padding: "7px 8px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                  }}
                  title={!formHasId() ? "Selecteer eerst een personeelslid" : "Documentsets voor dit personeelslid"}
                >
                  📚 Documentsets
                </button>

                <button
                  onClick={saveItem}
                  disabled={isFormLocked || saveBusy}
                  style={{
                    ...(isFormLocked || saveBusy
                      ? { ...buttonPrimary("#2f7dd1"), opacity: 0.5, cursor: "not-allowed" }
                      : buttonPrimary("#2f7dd1")),
                    width: "100%",
                    minWidth: 0,
                    height: 32,
                    padding: "7px 8px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {saveBusy ? "Opslaan..." : "💾 Opslaan"}
                </button>

                <button
                  onClick={deleteItem}
                  disabled={isFormLocked || !formHasId()}
                  style={{
                    ...(isFormLocked || !formHasId()
                      ? { ...buttonPrimary("#d64545"), opacity: 0.5, cursor: "not-allowed" }
                      : buttonPrimary("#d64545")),
                    width: "100%",
                    minWidth: 0,
                    height: 32,
                    padding: "7px 8px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  🗑 Verwijderen
                </button>

                <div />

                <button
                  onClick={() => {
                    vernieuwList();
                    positionPageTopNoKeyboard();
                  }}
                  style={{
                    ...buttonStyle,
                    width: "100%",
                    minWidth: 0,
                    height: 32,
                    padding: "7px 8px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  🔄 Vernieuw
                </button>
              </div>

              {matchingOpen && (
                <>
                  <hr style={{ margin: "10px 0" }} />

                  <div ref={matchingSectionRef} style={sectionTitleStyle}>Matching gegevens</div>

                  <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
                    {sortedMatchingRows.map((m) => (
                      <div
                        key={m.id}
                        style={{
                          border: "1px solid #e2e2e2",
                          borderRadius: 6,
                          padding: 8,
                          fontSize: 13,
                          background: "#fafafa",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                          <b>{m.category || "-"}</b>

                          <div style={{ display: "flex", gap: 4 }}>
                            <button
                              onClick={() => startEditMatchingRow(m)}
                              style={{
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                color: "#1f4e79",
                              }}
                              title="Wijzigen"
                            >
                              ✏️
                            </button>

                            <button
                              onClick={() => deleteMatchingRow(m.id)}
                              style={{
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                color: "#b00020",
                              }}
                              title="Verwijderen"
                            >
                              🗑
                            </button>
                          </div>
                        </div>
                        <div>{m.name || "-"}</div>
                        {m.description && <div style={{ opacity: 0.8 }}>{m.description}</div>}
                        <div style={{ opacity: 0.75 }}>
                          {m.level ? `Niveau: ${m.level}` : ""}
                          {m.diploma ? " · Diploma" : ""}
                        </div>
                        <div style={{ opacity: 0.75 }}>
                          {[formatDateNL(m.date_from), formatDateNL(m.date_to)].filter(Boolean).join(" t/m ")}
                        </div>
                        {m.employer && <div style={{ opacity: 0.75 }}>Werkgever: {m.employer}</div>}
                      </div>
                    ))}

                    {matchingRows.length === 0 && (
                      <div style={{ fontSize: 12, opacity: 0.7 }}>
                        Nog geen matchinggegevens.
                      </div>
                    )}
                  </div>

                  <div ref={matchingFormRef} style={{ marginTop: 12, display: "grid", gap: 6 }}>
                    <div style={sectionTitleStyle}>
                      {matchingEditId ? "Matchingregel wijzigen" : "Nieuwe matchingregel toevoegen"}
                    </div>

                    <label style={labelStyle}>Categorie</label>
                    <input
                      ref={matchingInputRef}
                      value={matchingDraft.category}
                      onChange={(e) => setMatchingDraft({ ...matchingDraft, category: e.target.value })}
                      style={inputStyle}
                      list="person_matching_category_list"
                    />

                    <label style={labelStyle}>Naam</label>
                    <input
                      value={matchingDraft.name}
                      onChange={(e) => setMatchingDraft({ ...matchingDraft, name: e.target.value })}
                      style={inputStyle}
                    />

                    <label style={labelStyle}>Omschrijving</label>
                    <textarea
                      value={matchingDraft.description}
                      onChange={(e) => setMatchingDraft({ ...matchingDraft, description: e.target.value })}
                      rows={3}
                      style={{ ...inputStyle, resize: "vertical" }}
                    />

                    <label style={labelStyle}>Niveau</label>
                    <input
                      value={matchingDraft.level}
                      onChange={(e) => setMatchingDraft({ ...matchingDraft, level: e.target.value })}
                      style={inputStyle}
                    />

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      <div>
                        <label style={labelStyle}>Datum van</label>
                        <input
                          type="date"
                          value={matchingDraft.date_from || ""}
                          onChange={(e) => setMatchingDraft({ ...matchingDraft, date_from: e.target.value })}
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Datum t/m</label>
                        <input
                          type="date"
                          value={matchingDraft.date_to || ""}
                          onChange={(e) => setMatchingDraft({ ...matchingDraft, date_to: e.target.value })}
                          style={inputStyle}
                        />
                      </div>
                    </div>

                    <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={matchingDraft.diploma === true}
                        onChange={(e) => setMatchingDraft({ ...matchingDraft, diploma: e.target.checked })}
                      />
                      Diploma
                    </label>

                    <label style={labelStyle}>Werkgever</label>
                    <input
                      value={matchingDraft.employer}
                      onChange={(e) => setMatchingDraft({ ...matchingDraft, employer: e.target.value })}
                      style={inputStyle}
                    />

                    <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                      <button
                        onClick={saveMatchingRow}
                        disabled={isReadonlyRole}
                        style={
                          isReadonlyRole
                            ? { ...buttonPrimary("#2f7dd1"), opacity: 0.5, cursor: "not-allowed" }
                            : buttonPrimary("#2f7dd1")
                        }
                      >
                        💾 Matching opslaan
                      </button>

                      <button onClick={closeMatchingBlock} style={buttonStyle}>
                        Annuleren
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>


      {documentSetsOpen && selectedId && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.35)",zIndex:999997,display:"flex",alignItems:"center",justifyContent:"center",padding:18}}>
          <div style={{width:"min(900px,100%)",maxHeight:"90vh",overflowY:"auto",background:"white",borderRadius:12,padding:18,boxSizing:"border-box"}}>
            <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"center",marginBottom:14}}><div><div style={{fontSize:20,fontWeight:700}}>📚 Documentsets</div><div style={{fontSize:13,opacity:.7}}>{form.search_name}</div></div><button onClick={()=>setDocumentSetsOpen(false)} style={buttonStyle}>✕ Sluiten</button></div>
            {documentSetsError && <div style={{color:"crimson",marginBottom:10}}>{documentSetsError}</div>}
            {documentSetsBusy && <div style={{marginBottom:10}}>Bezig...</div>}
            <b>Beschikbare documentsets</b>
            <div style={{display:"grid",gap:8,margin:"8px 0 18px"}}>{documentSets.map(set=>{const started=documentSetInstances.some(x=>String(x.document_set_id)===String(set.id));return <div key={set.id} style={{border:"1px solid #ddd",borderRadius:8,padding:10,display:"flex",justifyContent:"space-between",gap:10,alignItems:"center"}}><div><b>{set.name}</b>{set.description&&<div style={{fontSize:13,opacity:.7}}>{set.description}</div>}<div style={{fontSize:12,opacity:.65}}>{(set.items||[]).length} onderdelen</div></div><button disabled={started||documentSetsBusy||isReadonlyRole} onClick={()=>startPersonnelDocumentSet(set.id)} style={started||documentSetsBusy||isReadonlyRole?buttonDisabledStyle:buttonPrimary("#198754")}>{started?"✓ Gestart":"Start set"}</button></div>})}{!documentSets.length&&!documentSetsBusy&&<div style={{fontSize:13,opacity:.7}}>Geen actieve documentsets voor Personeel.</div>}</div>
            <b>Gestarte documentsets</b>
            <div style={{display:"grid",gap:12,marginTop:8}}>
              {documentSetInstances.map(inst=><div key={inst.id} style={{border:"1px solid #ddd",borderRadius:8,padding:12}}>
                <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                  <b>{inst.document_set_name}</b>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <span>{inst.progress_completed||0}/{inst.progress_total||0} gereed {inst.complete?"✓":""}</span>
                    <button
                      type="button"
                      disabled={isReadonlyRole||documentSetsBusy}
                      onClick={()=>deletePersonnelDocumentSet(inst)}
                      style={isReadonlyRole||documentSetsBusy?{...buttonPrimary("#d64545"),opacity:.5,cursor:"not-allowed"}:buttonPrimary("#d64545")}
                      title="Draai deze gestarte documentset terug. Documenten blijven behouden."
                    >
                      🗑 Verwijder set
                    </button>
                  </div>
                </div>
                <div style={{display:"grid",gap:7,marginTop:8}}>{(inst.items||[]).map(item=>{const docs=personnelDocuments.filter(d=>String(d.document_type_id||"")===String(item.document_type_id||""));return <div key={item.id} style={{background:item.required===false&&!item.document_id&&item.document_received!==true&&item.signature_received!==true?"#fafafa":item.completed?"#f2fbf2":"#fafafa",border:"1px solid #eee",borderRadius:6,padding:8}}><div style={{display:"flex",justifyContent:"space-between"}}><b>{item.title||item.document_type_name}{item.required!==false?" *":""}</b><span style={{fontSize:12}}>{item.required===false&&!item.document_id&&item.document_received!==true&&item.signature_received!==true?"Optioneel":item.completed?"✓ Gereed":"Open"}</span></div>{item.description&&<div style={{fontSize:12,opacity:.7}}>{item.description}</div>}{item.document_type_id&&<><label style={{fontSize:12}}>Document</label><div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) auto",gap:7,alignItems:"center"}}><select value={item.document_id||""} disabled={isReadonlyRole||documentSetsBusy} onChange={e=>updateDocumentSetItem(item,{document_id:e.target.value?Number(e.target.value):null,document_received:!!e.target.value})} style={selectStyle}><option value="">(nog niet ontvangen)</option>{docs.map(d=><option key={d.id} value={d.id}>{d.title ? `${d.title}${d.file_ext||""}` : (d.file_name||`Document ${d.id}`)}</option>)}</select><label style={{...buttonPrimary("#2f7dd1"),padding:"7px 10px",fontSize:12,opacity:isReadonlyRole||documentSetsBusy?.5:1,cursor:isReadonlyRole||documentSetsBusy?"not-allowed":"pointer"}} title="Nieuw document uploaden en direct aan dit onderdeel koppelen">📎 Upload<input type="file" style={{display:"none"}} disabled={isReadonlyRole||documentSetsBusy} onChange={e=>{const file=e.target.files?.[0]||null;e.target.value="";if(file)uploadDocumentForSetItem(item,file);}}/></label></div></>}{item.signature_required&&<label style={{display:"flex",gap:7,alignItems:"center",fontSize:12,marginTop:5}}><input type="checkbox" checked={item.signature_received===true} disabled={isReadonlyRole||documentSetsBusy} onChange={e=>updateDocumentSetItem(item,{signature_received:e.target.checked})}/> Ondertekening ontvangen</label>}{item.document_type_id&&!item.document_id&&!docs.length&&<div style={{fontSize:12,color:"#9a6500",marginTop:4}}>Nog geen document van type {item.document_type_name||"dit type"} aanwezig.</div>}</div>})}</div>
              </div>)}
              {!documentSetInstances.length&&!documentSetsBusy&&<div style={{fontSize:13,opacity:.7}}>Nog geen documentset gestart.</div>}
            </div>
          </div>
        </div>
      )}

      {dossierOpen && selectedId && (
        <PersoneelDossierPopup
          personnelId={selectedId}
          onClose={() => setDossierOpen(false)}
        />
      )}
    </div>
  );
}
